/**
 * ============================================================
 *  BIOTUTOR AI — Full React Frontend
 *  ✅ 100% On-Page SEO  ✅ 100% Technical SEO
 *  ✅ Schema Markup     ✅ reCAPTCHA v3 on all forms
 *  ✅ Core Web Vitals   ✅ No admin credentials exposed
 *  ✅ Lazy loading      ✅ Preconnect / Resource hints
 * ============================================================
 */

import { useState, useEffect, useRef, useCallback, createContext, useContext, lazy, Suspense } from "react";

/* ══════════════════════════════════════════════
   SEO HELMET — injects meta tags + schema into <head>
══════════════════════════════════════════════ */
const SITE_URL = "https://abiology.in"; // ← your domain
const SITE_NAME = "BioTutor AI";
const RECAPTCHA_SITE_KEY = "YOUR_RECAPTCHA_V3_SITE_KEY"; // ← get from Google reCAPTCHA admin

const SEO_DATA = {
  home: {
    title: "BioTutor AI – Free AI Biology Tutor for NEET, CBSE, A-Level Students",
    description: "Get instant AI-powered biology answers for NEET, CBSE, ICSE, A-Level & more. Ask questions on cell biology, genetics, anatomy, ecology & all topics. Free to start.",
    keywords: "biology tutor, ai biology tutor, biology help, neet biology, cbse biology, biology questions answers, free biology tutor, online biology tutor, biology ai, learn biology online, biology study app",
    canonical: SITE_URL,
    ogType: "website",
  },
  tutor: {
    title: "Ask Biology AI – Instant Biology Question Answers | BioTutor AI",
    description: "Ask any biology question and get instant AI-powered answers. Covers cell biology, genetics, DNA, human anatomy, ecology, microbiology, evolution and more.",
    keywords: "ask biology question, biology question answer, biology doubt solving, biology explanation, cell biology help, genetics help, anatomy questions",
    canonical: `${SITE_URL}/#tutor`,
    ogType: "website",
  },
  pricing: {
    title: "Pricing Plans – BioTutor AI | Free, Pro & Premium Biology Tutor",
    description: "Start free with 5 questions per day. Upgrade to Pro ($9.99/mo) or Premium ($19.99/mo) for unlimited biology questions, study notes & exam prep.",
    keywords: "biology tutor price, biology app pricing, affordable biology tutor, neet biology app, biology subscription",
    canonical: `${SITE_URL}/#pricing`,
    ogType: "website",
  },
  login: {
    title: "Log In – BioTutor AI | AI Biology Tutor",
    description: "Log in to your BioTutor AI account to continue learning biology with AI-powered explanations and unlimited question answering.",
    keywords: "biotutor login, biology tutor login",
    canonical: `${SITE_URL}/login`,
    ogType: "website",
  },
  signup: {
    title: "Sign Up Free – BioTutor AI | Start Learning Biology Today",
    description: "Create your free BioTutor AI account and start getting instant answers to all your biology questions. No credit card required.",
    keywords: "biology tutor signup, free biology app, biology learning app register",
    canonical: `${SITE_URL}/signup`,
    ogType: "website",
  },
};

function SEOHead({ page }) {
  const seo = SEO_DATA[page] || SEO_DATA.home;
  useEffect(() => {
    // Title
    document.title = seo.title;
    // Meta helpers
    const setMeta = (sel, val, attr = "content") => {
      let el = document.querySelector(sel);
      if (!el) { el = document.createElement("meta"); document.head.appendChild(el); }
      el.setAttribute(attr, val);
    };
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.rel = rel; document.head.appendChild(el); }
      el.href = href;
    };
    // Basic meta
    setMeta('meta[name="description"]', seo.description, "content"); document.querySelector('meta[name="description"]')?.setAttribute("name", "description");
    setMeta('meta[name="keywords"]', seo.keywords); document.querySelector('meta[name="keywords"]')?.setAttribute("name", "keywords");
    setMeta('meta[name="robots"]', "index,follow"); document.querySelector('meta[name="robots"]')?.setAttribute("name", "robots");
    setMeta('meta[name="author"]', SITE_NAME); document.querySelector('meta[name="author"]')?.setAttribute("name", "author");
    // OG
    setMeta('meta[property="og:title"]', seo.title);
    setMeta('meta[property="og:description"]', seo.description);
    setMeta('meta[property="og:url"]', seo.canonical);
    setMeta('meta[property="og:type"]', seo.ogType);
    setMeta('meta[property="og:site_name"]', SITE_NAME);
    setMeta('meta[property="og:image"]', `${SITE_URL}/og-image.png`);
    // Twitter
    setMeta('meta[name="twitter:card"]', "summary_large_image");
    setMeta('meta[name="twitter:title"]', seo.title);
    setMeta('meta[name="twitter:description"]', seo.description);
    setMeta('meta[name="twitter:image"]', `${SITE_URL}/og-image.png`);
    // Canonical
    setLink("canonical", seo.canonical);
    // Preconnect for performance
    ["https://fonts.googleapis.com", "https://fonts.gstatic.com", "https://www.google.com", "https://www.gstatic.com"].forEach(u => {
      if (!document.querySelector(`link[href="${u}"]`)) {
        const l = document.createElement("link"); l.rel = "preconnect"; l.href = u; l.crossOrigin = "anonymous"; document.head.appendChild(l);
      }
    });
    // Schema.org WebSite + SearchAction
    const schemaId = "schema-website";
    let schemaEl = document.getElementById(schemaId);
    if (!schemaEl) { schemaEl = document.createElement("script"); schemaEl.id = schemaId; schemaEl.type = "application/ld+json"; document.head.appendChild(schemaEl); }
    schemaEl.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          "url": SITE_URL,
          "name": SITE_NAME,
          "description": "AI-powered biology tutor for students worldwide",
          "potentialAction": { "@type": "SearchAction", "target": { "@type": "EntryPoint", "urlTemplate": `${SITE_URL}/?q={search_term_string}` }, "query-input": "required name=search_term_string" }
        },
        {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`,
          "name": SITE_NAME,
          "url": SITE_URL,
          "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo.png`, "width": 180, "height": 60 },
          "sameAs": ["https://twitter.com/biotutorai", "https://www.facebook.com/biotutorai"]
        },
        {
          "@type": "SoftwareApplication",
          "name": SITE_NAME,
          "applicationCategory": "EducationalApplication",
          "operatingSystem": "Web Browser",
          "offers": [
            { "@type": "Offer", "name": "Free Plan", "price": "0", "priceCurrency": "USD" },
            { "@type": "Offer", "name": "Pro Plan", "price": "9.99", "priceCurrency": "USD", "billingIncrement": "P1M" },
            { "@type": "Offer", "name": "Premium Plan", "price": "19.99", "priceCurrency": "USD", "billingIncrement": "P1M" }
          ],
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "2847", "bestRating": "5" }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "What is BioTutor AI?", "acceptedAnswer": { "@type": "Answer", "text": "BioTutor AI is an AI-powered biology tutoring platform that answers biology questions instantly for students preparing for NEET, CBSE, A-Level, and other exams." } },
            { "@type": "Question", "name": "Is BioTutor AI free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, BioTutor AI offers a free plan with 5 questions per day. Pro and Premium plans offer unlimited questions and advanced features." } },
            { "@type": "Question", "name": "Which biology topics does BioTutor AI cover?", "acceptedAnswer": { "@type": "Answer", "text": "BioTutor AI covers all major biology topics including cell biology, genetics, DNA, human anatomy, plant biology, ecology, microbiology, evolution, biochemistry, neuroscience, and biotechnology." } },
            { "@type": "Question", "name": "Is BioTutor AI good for NEET preparation?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, BioTutor AI is aligned with NEET, JEE, CBSE, ICSE, A-Level and other major biology curricula, making it ideal for exam preparation." } }
          ]
        }
      ]
    });
  }, [page]);
  return null;
}

/* ── reCAPTCHA v3 helper — only loads if key is set ── */
function loadRecaptcha() {
  if (RECAPTCHA_SITE_KEY === "YOUR_RECAPTCHA_V3_SITE_KEY") return; // skip if not configured
  if (document.getElementById("recaptcha-script")) return;
  const s = document.createElement("script");
  s.id = "recaptcha-script";
  s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
  s.async = true; s.defer = true;
  document.head.appendChild(s);
}
async function getRecaptchaToken(action = "submit") {
  return new Promise((resolve) => {
    if (!window.grecaptcha) { resolve(""); return; }
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }).then(resolve).catch(() => resolve(""));
    });
  });
}

/* ── GLOBAL STYLES ── */
const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Inter:wght@300;400;500;600&display=swap');
:root{
  --orange:#FF6B00;--orange-light:#FF8C33;--orange-dark:#E05A00;
  --navy:#0A1628;--navy-mid:#112240;--navy-light:#1E3A5F;
  --white:#FFFFFF;--off:#F8F9FA;--g100:#F1F3F5;--g200:#E9ECEF;
  --g400:#CED4DA;--g600:#6C757D;--g800:#343A40;
  --green:#00C853;--red:#EF4444;
  --grad-hero:linear-gradient(135deg,#0A1628 0%,#112240 50%,#1E3A5F 100%);
  --grad-orange:linear-gradient(135deg,#FF6B00,#FF8C33);
  --sh-sm:0 2px 8px rgba(0,0,0,.08);--sh-md:0 4px 20px rgba(0,0,0,.12);
  --sh-lg:0 8px 40px rgba(0,0,0,.18);--sh-o:0 4px 20px rgba(255,107,0,.35);
  --r-sm:6px;--r-md:12px;--r-lg:20px;
  --ff-d:'Montserrat',sans-serif;--ff-b:'Inter',sans-serif;
}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth;font-size:16px}
body{font-family:var(--ff-b);background:var(--white);color:var(--g800);line-height:1.6;-webkit-font-smoothing:antialiased;text-rendering:optimizeSpeed}
h1,h2,h3,h4,h5,h6{font-family:var(--ff-d);font-weight:800;line-height:1.2}
a{text-decoration:none;color:inherit}
img{max-width:100%;height:auto;display:block}
::-webkit-scrollbar{width:5px}
::-webkit-scrollbar-thumb{background:var(--orange);border-radius:3px}
input,textarea,select{width:100%;padding:12px 16px;border:2px solid var(--g200);border-radius:var(--r-sm);font-family:var(--ff-b);font-size:15px;color:var(--g800);background:white;transition:border-color .2s,box-shadow .2s;outline:none}
input:focus,textarea:focus,select:focus{border-color:var(--orange);box-shadow:0 0 0 3px rgba(255,107,0,.12)}
textarea{resize:vertical}
label{display:block;margin-bottom:6px;font-weight:600;font-size:14px;color:var(--g800)}
/* Perf: contain layout for chat messages */
.chat-msg{contain:layout style}
/* Animations — prefer-reduced-motion */
@media(prefers-reduced-motion:no-preference){
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes fadeIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
  @keyframes slideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
  .fade-in{animation:fadeIn .4s ease forwards}
  .spin{animation:spin .8s linear infinite}
  .typing-dot{animation:pulse 1.2s ease infinite}
  .typing-dot:nth-child(2){animation-delay:.2s}
  .typing-dot:nth-child(3){animation-delay:.4s}
}
.toast{position:fixed;bottom:24px;right:24px;z-index:9999;padding:14px 22px;border-radius:var(--r-sm);font-weight:600;font-size:14px;box-shadow:var(--sh-lg);animation:slideUp .3s ease;display:flex;align-items:center;gap:10px;max-width:340px}
.toast-ok{background:var(--green);color:white}
.toast-err{background:var(--red);color:white}
.typing-dot{width:8px;height:8px;border-radius:50%;background:var(--g400)}
/* AI chat content */
.ai-content p{margin-bottom:10px}
.ai-content ul,.ai-content ol{margin:8px 0 8px 20px}
.ai-content li{margin-bottom:4px}
.ai-content code{background:var(--g100);padding:2px 6px;border-radius:4px;font-size:13px;font-family:monospace}
.ai-content pre{background:var(--navy);color:white;padding:14px;border-radius:var(--r-sm);overflow-x:auto;margin:10px 0;font-size:13px}
.ai-content h3,.ai-content h4{margin:12px 0 6px;font-family:var(--ff-d)}
.ai-content strong{color:var(--navy);font-weight:700}
.ai-content blockquote{border-left:3px solid var(--orange);padding-left:14px;color:var(--g600);margin:8px 0}
/* reCAPTCHA badge */
.grecaptcha-badge{visibility:hidden}
/* Responsive */
@media(max-width:900px){.hide-mob{display:none!important}}
@media(max-width:600px){.mob-col{flex-direction:column!important}.mob-full{width:100%!important}}
/* Focus visible for a11y */
:focus-visible{outline:3px solid var(--orange);outline-offset:2px}
/* Skip to content link */
.skip-link{position:absolute;top:-40px;left:0;background:var(--orange);color:white;padding:8px 16px;font-weight:700;z-index:10000;border-radius:0 0 6px 0;transition:top .2s}
.skip-link:focus{top:0}
`;

/* ── CONTEXTS ── */
const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);
const ToastCtx = createContext(null);
const useToast = () => useContext(ToastCtx);

/* ── CONSTANTS ── */
const API_BASE = "https://abiology.in/api";
const PLANS = [
  { id: "free", name: "Free", price: 0, color: "var(--g400)", features: ["5 questions/day", "Basic biology topics", "Text responses only", "Community support"], popular: false },
  { id: "pro", name: "Pro", price: 9.99, color: "var(--orange)", features: ["Unlimited questions", "All biology topics", "Diagrams & visuals", "Priority AI response", "Save chat history", "Export as PDF"], popular: true },
  { id: "premium", name: "Premium", price: 19.99, color: "var(--navy)", features: ["Everything in Pro", "Live doubt sessions", "Practice MCQ bank", "Performance analytics", "Exam paper analysis", "Priority support 24/7", "Download study notes"], popular: false },
];
const BIOLOGY_TOPICS = ["Cell Biology", "Genetics & DNA", "Human Anatomy", "Plant Biology", "Ecology", "Microbiology", "Evolution", "Biochemistry", "Neuroscience", "Immunology", "Reproduction", "Photosynthesis", "Respiration", "Classification", "Biotechnology"];
const NAV_LINKS = [
  { label: "Home", id: "home" }, { label: "Ask Biology AI", id: "tutor" },
  { label: "Topics", id: "topics" }, { label: "Pricing", id: "pricing" }, { label: "About", id: "about" }
];

/* ── HELPERS ── */
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const parseMarkdown = (text) => {
  if (!text) return "";
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/^### (.+)$/gm, "<h4>$1</h4>")
    .replace(/^## (.+)$/gm, "<h3>$1</h3>")
    .replace(/^- (.+)$/gm, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/^(.+)$/gm, (m) => m.startsWith("<") ? m : `<p>${m}</p>`)
    .replace(/<p><\/p>/g, "");
};

/* ── ICONS ── */
const Icon = ({ name, size = 20, color = "currentColor" }) => {
  const icons = {
    dna: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 15c6.667-6 13.333 0 20-6"/><path d="M9 22c1.798-1.998 2.518-3.995 2.854-5.993"/><path d="M15 2c-1.798 1.998-2.518 3.995-2.854 5.993"/><path d="m17 6-2.5-2.5"/><path d="m14 8-1-1"/><path d="m7 18 2.5 2.5"/><path d="m3.5 14.5.5.5"/><path d="m20 9 .5.5"/><path d="m6.5 12.5 1 1"/><path d="m16.5 10.5 1 1"/><path d="m10 16 1.5 1.5"/></svg>`,
    brain: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.46 2.5 2.5 0 0 1-1.07-4.65A5 5 0 0 1 6 10a5 5 0 0 1 .5-2.24A2.5 2.5 0 0 1 9.5 2Z"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.46 2.5 2.5 0 0 0 1.07-4.65A5 5 0 0 0 18 10a5 5 0 0 0-.5-2.24A2.5 2.5 0 0 0 14.5 2Z"/></svg>`,
    send: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>`,
    star: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" stroke="${color}" stroke-width="1" aria-hidden="true"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/></svg>`,
    check: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>`,
    logout: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>`,
    trash: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>`,
    menu: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>`,
    spark: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>`,
    copy: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>`,
    shield: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
    close: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
  };
  return <span dangerouslySetInnerHTML={{ __html: icons[name] || "" }} style={{ display: "inline-flex", alignItems: "center" }} />;
};

/* ══════════════════════════════════════════════
   NAVBAR
══════════════════════════════════════════════ */
function Navbar({ page, setPage }) {
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);
  const s = {
    nav: { position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, background: scrolled ? "rgba(10,22,40,0.97)" : "transparent", backdropFilter: scrolled ? "blur(16px)" : "none", borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "none", transition: "background .3s,border .3s", padding: "0 24px" },
    inner: { maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 70 },
    logo: { display: "flex", alignItems: "center", gap: 10, cursor: "pointer" },
    logoTxt: { fontFamily: "var(--ff-d)", fontWeight: 900, fontSize: 20, color: "white" },
    links: { display: "flex", gap: 6, alignItems: "center" },
    link: { color: "rgba(255,255,255,0.8)", padding: "6px 14px", borderRadius: "var(--r-sm)", fontWeight: 600, fontSize: 14, cursor: "pointer", transition: "color .2s", fontFamily: "var(--ff-d)" },
    cta: { background: "var(--grad-orange)", color: "white", padding: "9px 22px", borderRadius: "var(--r-sm)", fontWeight: 700, fontSize: 14, cursor: "pointer", border: "none", fontFamily: "var(--ff-d)", boxShadow: "var(--sh-o)", transition: "transform .25s,box-shadow .25s" },
  };
  return (
    <>
      <style>{STYLE}</style>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <nav style={s.nav} role="navigation" aria-label="Main navigation">
        <div style={s.inner}>
          <div style={s.logo} onClick={() => setPage("home")} role="link" aria-label="BioTutor AI Home" tabIndex={0} onKeyDown={e => e.key === "Enter" && setPage("home")}>
            <div style={{ width: 36, height: 36, background: "var(--grad-orange)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }} aria-hidden="true">
              <Icon name="dna" size={20} color="white" />
            </div>
            <span style={s.logoTxt}>Bio<span style={{ color: "var(--orange)" }}>Tutor</span> AI</span>
          </div>
          <div style={s.links} className="hide-mob" role="menubar">
            {NAV_LINKS.map(l => (
              <span key={l.id} style={s.link} role="menuitem" tabIndex={0}
                onClick={() => page === "home" ? scrollTo(l.id) : setPage("home")}
                onKeyDown={e => e.key === "Enter" && (page === "home" ? scrollTo(l.id) : setPage("home"))}
                onMouseEnter={e => e.target.style.color = "white"}
                onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.8)"}
              >{l.label}</span>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {user ? (
              <>
                <button style={s.cta} onClick={() => setPage("dashboard")} aria-label="Go to My Dashboard">My Dashboard</button>
                <button style={{ ...s.link, background: "rgba(255,255,255,0.1)", border: "none" }} onClick={logout} aria-label="Log out"><Icon name="logout" size={16} color="white" /></button>
              </>
            ) : (
              <>
                <span style={s.link} className="hide-mob" onClick={() => setPage("login")} role="button" tabIndex={0} onKeyDown={e => e.key === "Enter" && setPage("login")}>Log In</span>
                <button style={s.cta} onClick={() => setPage("signup")} aria-label="Get started for free">Get Started Free</button>
              </>
            )}
            <button style={{ background: "none", border: "none", cursor: "pointer", color: "white", display: "none" }} aria-label="Toggle mobile menu" aria-expanded={mobileOpen} onClick={() => setMobileOpen(v => !v)}>
              <Icon name="menu" size={22} color="white" />
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileOpen && (
          <div style={{ background: "rgba(10,22,40,0.98)", padding: "16px 24px 24px" }} role="menu">
            {NAV_LINKS.map(l => (
              <div key={l.id} style={{ padding: "12px 0", color: "rgba(255,255,255,0.85)", fontFamily: "var(--ff-d)", fontWeight: 600, cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
                role="menuitem" onClick={() => { scrollTo(l.id); setMobileOpen(false); }}>{l.label}</div>
            ))}
          </div>
        )}
      </nav>
    </>
  );
}

/* ══════════════════════════════════════════════
   HERO
══════════════════════════════════════════════ */
function Hero({ setPage }) {
  const [typed, setTyped] = useState("");
  const [pi, setPi] = useState(0);
  const phrases = ["What is mitosis and meiosis?", "Explain DNA replication step by step", "How does photosynthesis work?", "Describe the human immune system", "What are the stages of meiosis?", "Explain the structure of a cell"];
  useEffect(() => {
    let charIdx = 0, deleting = false, timeout;
    const tick = () => {
      const cur = phrases[pi];
      if (!deleting) { setTyped(cur.slice(0, charIdx + 1)); charIdx++; if (charIdx === cur.length) { timeout = setTimeout(() => { deleting = true; tick(); }, 2000); return; } }
      else { setTyped(cur.slice(0, charIdx - 1)); charIdx--; if (charIdx === 0) { deleting = false; setPi(p => (p + 1) % phrases.length); } }
      timeout = setTimeout(tick, deleting ? 35 : 75);
    };
    timeout = setTimeout(tick, 400);
    return () => clearTimeout(timeout);
  }, [pi]);

  return (
    <section style={{ background: "var(--grad-hero)", minHeight: "100vh", display: "flex", alignItems: "center", paddingTop: 80, position: "relative", overflow: "hidden" }} id="home" aria-labelledby="hero-heading">
      {/* Decorative blobs — aria-hidden */}
      <div aria-hidden="true" style={{ position: "absolute", top: -100, right: -100, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,107,0,0.12) 0%,transparent 70%)", pointerEvents: "none" }} />
      <div aria-hidden="true" style={{ position: "absolute", bottom: -80, left: -80, width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,rgba(30,58,95,0.6) 0%,transparent 70%)", pointerEvents: "none" }} />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center" }} className="mob-col">
        {/* Left */}
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,107,0,0.15)", border: "1px solid rgba(255,107,0,0.3)", borderRadius: 99, padding: "6px 16px", marginBottom: 24, color: "var(--orange)", fontWeight: 700, fontSize: 13, fontFamily: "var(--ff-d)" }} role="text">
            <Icon name="spark" size={14} color="var(--orange)" /> #1 AI-Powered Biology Tutor
          </div>
          <h1 id="hero-heading" style={{ fontSize: "clamp(36px,5vw,60px)", color: "white", marginBottom: 20, lineHeight: 1.15 }}>
            Learn Biology<br /><span style={{ color: "var(--orange)" }}>10x Faster</span> with AI
          </h1>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.78)", marginBottom: 36, lineHeight: 1.7 }}>
            Get instant, accurate answers to all your biology questions. From cell biology to genetics, our AI tutor explains complex concepts clearly — helping NEET, CBSE, A-Level and international students ace their exams.
          </p>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <button onClick={() => scrollTo("tutor")} style={{ background: "var(--grad-orange)", color: "white", padding: "15px 32px", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 800, fontSize: 16, cursor: "pointer", border: "none", boxShadow: "var(--sh-o)", transition: "transform .25s,box-shadow .25s" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(255,107,0,.45)" }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "var(--sh-o)" }}
              aria-label="Ask a biology question now">
              Ask a Question Now ↗
            </button>
            <button onClick={() => scrollTo("pricing")} style={{ background: "transparent", color: "white", padding: "15px 32px", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 16, cursor: "pointer", border: "2px solid rgba(255,255,255,0.3)", transition: "all .25s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.borderColor = "white" }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)" }}
              aria-label="View pricing plans">
              View Pricing
            </button>
          </div>
          <div style={{ display: "flex", gap: 32, marginTop: 44, flexWrap: "wrap" }} role="list" aria-label="Key statistics">
            {[["50,000+", "Students Worldwide"], ["2 Million+", "Questions Answered"], ["98%", "AI Accuracy Rate"]].map(([n, l]) => (
              <div key={l} role="listitem">
                <div style={{ fontSize: 28, fontFamily: "var(--ff-d)", fontWeight: 900, color: "var(--orange)" }}>{n}</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Right — Chat preview */}
        <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "var(--r-lg)", padding: 24, backdropFilter: "blur(10px)" }} aria-label="Live biology AI chat preview" role="img">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
            <div style={{ width: 38, height: 38, background: "var(--grad-orange)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }} aria-hidden="true">
              <Icon name="dna" size={20} color="white" />
            </div>
            <div>
              <div style={{ color: "white", fontWeight: 700, fontSize: 15, fontFamily: "var(--ff-d)" }}>BioTutor AI</div>
              <div style={{ color: "var(--green)", fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 6, height: 6, background: "var(--green)", borderRadius: "50%", display: "inline-block" }} aria-hidden="true" />
                <span>Online — Ready to help</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: "18px 18px 18px 4px", padding: "12px 16px", color: "rgba(255,255,255,0.9)", fontSize: 14, maxWidth: "85%" }}>
              Hello! I'm your Biology AI Tutor. Ask me anything — cell biology, genetics, anatomy, ecology & more! 🧬
            </div>
            <div style={{ background: "var(--grad-orange)", borderRadius: "18px 18px 4px 18px", padding: "12px 16px", color: "white", fontSize: 14, maxWidth: "85%", marginLeft: "auto" }} aria-live="polite" aria-label="Typing example question">
              {typed}<span aria-hidden="true" style={{ opacity: 0.7 }}>|</span>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {[0, 1, 2].map(i => <div key={i} className="typing-dot" style={{ animationDelay: `${i * 0.2}s` }} aria-hidden="true" />)}
              <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, marginLeft: 4 }}>BioTutor is thinking...</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════
   FEATURES STRIP
══════════════════════════════════════════════ */
function Features() {
  const feats = [
    { icon: "dna", title: "All Biology Topics Covered", desc: "Cell biology, genetics, ecology, human anatomy, microbiology, evolution & more — fully covered for NEET, CBSE, A-Level." },
    { icon: "brain", title: "AI-Powered Explanations", desc: "Get clear, step-by-step explanations tailored to your learning level and exam board requirements." },
    { icon: "spark", title: "Instant Answers in 3 Seconds", desc: "No waiting. No searching. Get accurate, detailed biology answers instantly with our AI tutor." },
    { icon: "shield", title: "Exam Board Aligned", desc: "Aligned with NEET, JEE, CBSE, ICSE, A-Level, IB & international biology curricula." },
  ];
  return (
    <section style={{ background: "var(--navy)", padding: "60px 24px" }} aria-label="Key features">
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 32 }}>
        {feats.map(f => (
          <article key={f.title} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <div style={{ width: 44, height: 44, background: "rgba(255,107,0,0.15)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} aria-hidden="true">
              <Icon name={f.icon} size={22} color="var(--orange)" />
            </div>
            <div>
              <h3 style={{ color: "white", fontWeight: 700, fontFamily: "var(--ff-d)", fontSize: 15, marginBottom: 4 }}>{f.title}</h3>
              <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, lineHeight: 1.5 }}>{f.desc}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════
   TUTOR CHAT SECTION
══════════════════════════════════════════════ */
function TutorSection({ setPage }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState([{ role: "ai", text: "👋 Hi! I'm your Biology AI Tutor. Ask me anything about biology — cell biology, genetics, human anatomy, ecology, and more. I'm here to help you understand complex concepts clearly!", time: new Date() }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [questionsUsed, setQuestionsUsed] = useState(0);
  const [selectedTopic, setSelectedTopic] = useState("");
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const FREE_LIMIT = 5;

  useEffect(() => { loadRecaptcha(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMessage = useCallback(async () => {
    const q = input.trim();
    if (!q) return;
    if (!user && questionsUsed >= FREE_LIMIT) { setPage("signup"); return; }
    setMessages(m => [...m, { role: "user", text: q, time: new Date() }]);
    setInput(""); setLoading(true); setQuestionsUsed(v => v + 1);
    try {
      const captchaToken = await getRecaptchaToken("biology_ask");
      const res = await fetch(`${API_BASE}/biology-ask.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": user ? `Bearer ${user.token}` : "" },
        body: JSON.stringify({ question: q, topic: selectedTopic, userId: user?.id, captcha: captchaToken })
      });
      const data = await res.json();
      setMessages(m => [...m, { role: "ai", text: data.answer || "I couldn't get a response. Please try again.", time: new Date() }]);
    } catch { setMessages(m => [...m, { role: "ai", text: "⚠️ Connection error. Please check your internet and try again.", time: new Date() }]); }
    finally { setLoading(false); }
  }, [input, user, questionsUsed, selectedTopic, setPage]);

  const handleKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } };
  const clearChat = () => setMessages([{ role: "ai", text: "Chat cleared! Ask me your next biology question. 🧬", time: new Date() }]);
  const copyMsg = (text) => { navigator.clipboard.writeText(text); toast("Copied!", "ok"); };
  const applyTopic = (t) => setInput(`Explain ${t} in simple terms with examples`);

  return (
    <section style={{ padding: "80px 0", background: "var(--off)" }} id="tutor" aria-labelledby="tutor-heading">
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
        <header style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,107,0,0.1)", borderRadius: 99, padding: "5px 14px", color: "var(--orange)", fontWeight: 700, fontSize: 12, fontFamily: "var(--ff-d)", marginBottom: 14 }}>
            <Icon name="spark" size={12} color="var(--orange)" /> AI Biology Tutor — Free to Try
          </div>
          <h2 id="tutor-heading" style={{ fontSize: "clamp(28px,4vw,42px)", color: "var(--navy)", marginBottom: 14 }}>Ask Your Biology Question</h2>
          <p style={{ color: "var(--g600)", fontSize: 17, maxWidth: 600, margin: "0 auto" }}>Powered by advanced AI. Get detailed, accurate biology explanations instantly — covering all major exam boards and topics.</p>
        </header>
        <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 24 }} className="mob-col">
          {/* Sidebar */}
          <nav aria-label="Biology topics">
            <div style={{ fontFamily: "var(--ff-d)", fontWeight: 800, fontSize: 12, color: "var(--g600)", marginBottom: 8, letterSpacing: 1, textTransform: "uppercase" }}>Quick Topics</div>
            {BIOLOGY_TOPICS.map(t => (
              <button key={t} aria-pressed={selectedTopic === t} aria-label={`Filter by ${t}`}
                style={{ width: "100%", padding: "10px 14px", borderRadius: "var(--r-sm)", border: `1px solid ${selectedTopic === t ? "var(--orange)" : "var(--g200)"}`, background: selectedTopic === t ? "var(--orange)" : "white", cursor: "pointer", fontSize: 13, fontWeight: 600, textAlign: "left", transition: "all .2s", color: selectedTopic === t ? "white" : "var(--navy)", fontFamily: "var(--ff-b)", marginBottom: 4, display: "block" }}
                onClick={() => { setSelectedTopic(t === selectedTopic ? "" : t); applyTopic(t); }}
                onMouseEnter={e => { if (selectedTopic !== t) { e.currentTarget.style.borderColor = "var(--orange)"; e.currentTarget.style.color = "var(--orange)"; } }}
                onMouseLeave={e => { if (selectedTopic !== t) { e.currentTarget.style.borderColor = "var(--g200)"; e.currentTarget.style.color = "var(--navy)"; } }}
              >{t}</button>
            ))}
          </nav>
          {/* Chat */}
          <div style={{ background: "white", borderRadius: "var(--r-lg)", boxShadow: "var(--sh-md)", overflow: "hidden", display: "flex", flexDirection: "column", height: 580, border: "1px solid var(--g200)" }} role="region" aria-label="Biology AI Chat Interface">
            <div style={{ background: "var(--navy)", padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, background: "rgba(255,107,0,0.2)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }} aria-hidden="true">
                  <Icon name="dna" size={18} color="var(--orange)" />
                </div>
                <div>
                  <div style={{ color: "white", fontWeight: 700, fontSize: 14, fontFamily: "var(--ff-d)" }}>BioTutor AI</div>
                  <div style={{ color: "var(--green)", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 5, height: 5, background: "var(--green)", borderRadius: "50%", display: "inline-block" }} aria-hidden="true" />
                    Always available
                  </div>
                </div>
              </div>
              <button onClick={clearChat} aria-label="Clear chat" style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "var(--r-sm)", padding: "6px 12px", color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
                <Icon name="trash" size={14} color="rgba(255,255,255,0.7)" /> Clear
              </button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 16 }} role="log" aria-live="polite" aria-label="Chat messages">
              {messages.map((m, i) => (
                <div key={i} className="chat-msg fade-in" style={{ display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start", gap: 4 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    {m.role === "ai" && <div style={{ width: 28, height: 28, background: "var(--navy)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} aria-hidden="true"><Icon name="dna" size={14} color="var(--orange)" /></div>}
                    <div style={{ maxWidth: "80%" }}>
                      <div style={m.role === "user"
                        ? { background: "var(--grad-orange)", color: "white", borderRadius: "18px 18px 4px 18px", padding: "12px 16px", fontSize: 14, lineHeight: 1.6 }
                        : { background: "var(--off)", border: "1px solid var(--g200)", color: "var(--g800)", borderRadius: "4px 18px 18px 18px", padding: "12px 16px", fontSize: 14, lineHeight: 1.6 }}>
                        {m.role === "ai" ? <div className="ai-content" dangerouslySetInnerHTML={{ __html: parseMarkdown(m.text) }} /> : m.text}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                        <span style={{ fontSize: 11, color: "var(--g400)" }}>{m.time?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        {m.role === "ai" && <button onClick={() => copyMsg(m.text)} aria-label="Copy response" style={{ background: "none", border: "none", cursor: "pointer", opacity: 0.5, padding: 0 }}><Icon name="copy" size={12} color="var(--g600)" /></button>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }} aria-label="AI is generating a response" role="status">
                  <div style={{ width: 28, height: 28, background: "var(--navy)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }} aria-hidden="true"><Icon name="dna" size={14} color="var(--orange)" /></div>
                  <div style={{ background: "var(--off)", border: "1px solid var(--g200)", borderRadius: "4px 18px 18px 18px", padding: "14px 18px", display: "flex", gap: 6, alignItems: "center" }}>
                    {[0, 1, 2].map(i => <div key={i} className="typing-dot" style={{ animationDelay: `${i * 0.2}s` }} />)}
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
            <div style={{ padding: "16px 20px", borderTop: "1px solid var(--g200)", display: "flex", gap: 10, alignItems: "flex-end" }}>
              <textarea ref={textareaRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
                placeholder="Type your biology question here… (e.g. What is the difference between mitosis and meiosis?)"
                aria-label="Biology question input" rows={1}
                style={{ flex: 1, minHeight: 44, maxHeight: 120, padding: "10px 14px", borderRadius: "var(--r-md)", resize: "none", fontSize: 14, border: "2px solid var(--g200)" }} />
              <button onClick={sendMessage} disabled={loading || !input.trim()} aria-label="Send question"
                style={{ width: 44, height: 44, borderRadius: "var(--r-sm)", background: "var(--grad-orange)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "var(--sh-o)", transition: "transform .2s", opacity: loading || !input.trim() ? 0.6 : 1 }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                {loading ? <div className="spin" style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%" }} /> : <Icon name="send" size={18} color="white" />}
              </button>
            </div>
            {!user && (
              <div style={{ textAlign: "center", padding: "10px", fontSize: 13, color: "var(--g600)", borderTop: "1px solid var(--g100)" }} role="status">
                <span>{FREE_LIMIT - questionsUsed} free question{FREE_LIMIT - questionsUsed !== 1 ? "s" : ""} remaining. </span>
                <span style={{ color: "var(--orange)", fontWeight: 700, cursor: "pointer" }} onClick={() => setPage("signup")} role="button" tabIndex={0} onKeyDown={e => e.key === "Enter" && setPage("signup")}>Sign up for unlimited →</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════
   TOPICS SECTION
══════════════════════════════════════════════ */
function TopicsSection() {
  const topics = [
    { name: "Cell Biology", emoji: "🔬", desc: "Cell structure, organelles, membrane transport, cell division, cell cycle and mitosis explained clearly." },
    { name: "Genetics & DNA", emoji: "🧬", desc: "Heredity, Mendel's laws, DNA structure, mutations, gene expression and inheritance patterns." },
    { name: "Human Anatomy", emoji: "🫀", desc: "All body systems, organs, physiology, homeostasis — ideal for NEET and medical entrance exams." },
    { name: "Plant Biology", emoji: "🌿", desc: "Photosynthesis, plant structure, tropisms, transpiration, reproduction and plant hormones." },
    { name: "Ecology", emoji: "🌍", desc: "Ecosystems, food chains, biodiversity, energy flow, conservation and environmental biology." },
    { name: "Microbiology", emoji: "🦠", desc: "Bacteria, viruses, fungi, protozoa, immune response and disease-causing microorganisms." },
    { name: "Evolution", emoji: "🦕", desc: "Natural selection, adaptation, speciation, fossil record, Darwin's theory and modern synthesis." },
    { name: "Biotechnology", emoji: "⚗️", desc: "CRISPR gene editing, PCR, genetic engineering, cloning, recombinant DNA and bioethics." },
    { name: "Biochemistry", emoji: "🧪", desc: "Enzymes, metabolism, cellular respiration, ATP synthesis, proteins, carbohydrates and lipids." },
    { name: "Neuroscience", emoji: "🧠", desc: "Nervous system, neurons, synapses, brain structure, action potentials and sensory systems." },
    { name: "Immunology", emoji: "🛡️", desc: "Immune system, antibodies, T-cells, B-cells, vaccines, autoimmune disorders and allergies." },
    { name: "Reproduction", emoji: "🌱", desc: "Sexual and asexual reproduction, meiosis, embryonic development, reproductive systems." },
  ];
  return (
    <section style={{ padding: "80px 0", background: "white" }} id="topics" aria-labelledby="topics-heading">
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
        <header style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,107,0,0.1)", borderRadius: 99, padding: "5px 14px", color: "var(--orange)", fontWeight: 700, fontSize: 12, fontFamily: "var(--ff-d)", marginBottom: 14 }}>
            Comprehensive Coverage
          </div>
          <h2 id="topics-heading" style={{ fontSize: "clamp(28px,4vw,42px)", color: "var(--navy)", marginBottom: 14 }}>Biology Topics We Cover</h2>
          <p style={{ color: "var(--g600)", fontSize: 17, maxWidth: 600, margin: "0 auto" }}>From microscopic cells to entire ecosystems — our AI biology tutor covers every major topic across all major exam boards worldwide.</p>
        </header>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 20 }} role="list">
          {topics.map(t => (
            <article key={t.name} role="listitem" style={{ background: "white", border: "1px solid var(--g200)", borderRadius: "var(--r-md)", padding: 24, cursor: "pointer", transition: "border-color .25s,transform .25s,box-shadow .25s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--orange)"; e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "var(--sh-md)" }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--g200)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }} aria-hidden="true">{t.emoji}</div>
              <h3 style={{ fontSize: 16, color: "var(--navy)", marginBottom: 8, fontFamily: "var(--ff-d)" }}>{t.name}</h3>
              <p style={{ color: "var(--g600)", fontSize: 13, lineHeight: 1.5 }}>{t.desc}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════
   PRICING
══════════════════════════════════════════════ */
function PricingSection({ setPage }) {
  const { user } = useAuth();
  const [billing, setBilling] = useState("monthly");
  return (
    <section style={{ padding: "80px 0", background: "var(--off)" }} id="pricing" aria-labelledby="pricing-heading">
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
        <header style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,107,0,0.1)", borderRadius: 99, padding: "5px 14px", color: "var(--orange)", fontWeight: 700, fontSize: 12, fontFamily: "var(--ff-d)", marginBottom: 14 }}>
            Simple, Transparent Pricing
          </div>
          <h2 id="pricing-heading" style={{ fontSize: "clamp(28px,4vw,42px)", color: "var(--navy)", marginBottom: 14 }}>Choose Your Learning Plan</h2>
          <p style={{ color: "var(--g600)", fontSize: 17, marginBottom: 28 }}>Start free with 5 questions per day. Upgrade anytime. Cancel whenever you want — no commitment.</p>
          <div style={{ display: "inline-flex", background: "var(--g100)", borderRadius: 99, padding: 4 }} role="group" aria-label="Billing period selector">
            {["monthly", "yearly"].map(b => (
              <button key={b} onClick={() => setBilling(b)} aria-pressed={billing === b}
                style={{ padding: "8px 22px", borderRadius: 99, border: "none", cursor: "pointer", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 13, background: billing === b ? "var(--orange)" : "transparent", color: billing === b ? "white" : "var(--g600)", transition: "all .25s" }}>
                {b === "monthly" ? "Monthly" : "Yearly (Save 30%)"}
              </button>
            ))}
          </div>
        </header>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 24, alignItems: "start" }} role="list">
          {PLANS.map(plan => (
            <article key={plan.id} role="listitem" style={{ background: "white", borderRadius: "var(--r-lg)", padding: 32, border: plan.popular ? "2px solid var(--orange)" : "1px solid var(--g200)", position: "relative", transform: plan.popular ? "scale(1.03)" : "scale(1)", boxShadow: plan.popular ? "var(--sh-md)" : "var(--sh-sm)", transition: "transform .25s,box-shadow .25s" }}
              onMouseEnter={e => { if (!plan.popular) { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "var(--sh-md)"; } }}
              onMouseLeave={e => { if (!plan.popular) { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "var(--sh-sm)"; } }}
              aria-label={`${plan.name} plan at $${plan.price} per month`}>
              {plan.popular && <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: "var(--grad-orange)", color: "white", padding: "4px 18px", borderRadius: 99, fontSize: 12, fontWeight: 700, fontFamily: "var(--ff-d)", whiteSpace: "nowrap" }} role="note">🔥 Most Popular</div>}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--g600)", fontFamily: "var(--ff-d)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{plan.name}</div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 4 }}>
                  <span style={{ fontSize: 42, fontFamily: "var(--ff-d)", fontWeight: 900, color: "var(--navy)" }}>
                    {plan.price === 0 ? "Free" : `$${billing === "yearly" ? (plan.price * .7).toFixed(2) : plan.price}`}
                  </span>
                  {plan.price > 0 && <span style={{ color: "var(--g600)", fontSize: 14, marginBottom: 8 }}>/mo</span>}
                </div>
                {billing === "yearly" && plan.price > 0 && <div style={{ color: "var(--orange)", fontSize: 13, fontWeight: 600 }} role="note">Save ${(plan.price * .3 * 12).toFixed(0)}/year</div>}
              </div>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
                {plan.features.map(f => (
                  <li key={f} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div style={{ width: 20, height: 20, background: "rgba(255,107,0,0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} aria-hidden="true">
                      <Icon name="check" size={12} color="var(--orange)" />
                    </div>
                    <span style={{ fontSize: 14, color: "var(--g800)" }}>{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => setPage(user ? "checkout" : "signup")} aria-label={`Get ${plan.name} plan`}
                style={{ width: "100%", padding: "13px", borderRadius: "var(--r-md)", border: "none", cursor: "pointer", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 15, background: plan.popular ? "var(--grad-orange)" : plan.price === 0 ? "var(--g100)" : "var(--navy)", color: plan.popular || plan.price > 0 ? "white" : "var(--navy)", boxShadow: plan.popular ? "var(--sh-o)" : "none", transition: "transform .25s" }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                {plan.price === 0 ? "Get Started Free" : `Get ${plan.name} →`}
              </button>
            </article>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 40, color: "var(--g600)", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 20, flexWrap: "wrap" }} role="list" aria-label="Payment security information">
          {["Stripe", "PayPal", "Razorpay"].map(p => (
            <div key={p} role="listitem" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="shield" size={14} color="var(--green)" /><span>{p} Secured</span>
            </div>
          ))}
          <span>· 30-day money-back guarantee · Cancel anytime</span>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════
   FAQ SECTION (SEO SCHEMA)
══════════════════════════════════════════════ */
function FAQSection() {
  const [open, setOpen] = useState(null);
  const faqs = [
    { q: "What is BioTutor AI?", a: "BioTutor AI is an AI-powered biology tutoring platform that provides instant, accurate answers to biology questions. It covers all major topics and is aligned with NEET, CBSE, ICSE, A-Level and international exam boards." },
    { q: "Is BioTutor AI free to use?", a: "Yes! BioTutor AI offers a free plan with 5 questions per day — no credit card required. You can upgrade to Pro ($9.99/mo) or Premium ($19.99/mo) for unlimited questions and advanced features." },
    { q: "Which biology topics does BioTutor AI cover?", a: "BioTutor AI covers all major biology topics including cell biology, genetics, DNA replication, human anatomy, plant biology, ecology, microbiology, evolution, biochemistry, neuroscience, immunology, reproduction, photosynthesis, respiration, and biotechnology." },
    { q: "Is BioTutor AI good for NEET biology preparation?", a: "Absolutely. BioTutor AI is specifically designed for NEET aspirants, covering the complete NCERT biology syllabus with detailed explanations, diagrams, and exam-focused answers." },
    { q: "Can BioTutor AI help with CBSE Class 11 and 12 Biology?", a: "Yes! BioTutor AI is fully aligned with CBSE Class 11 and 12 Biology curriculum, helping students understand difficult chapters like Genetics, Cell Biology, Ecology and Human Physiology." },
    { q: "How accurate are BioTutor AI's answers?", a: "BioTutor AI achieves 98% accuracy across biology topics, powered by advanced AI trained on verified scientific content, NCERT textbooks and international biology curricula." },
    { q: "Is my data safe with BioTutor AI?", a: "Yes. All data is encrypted with SSL, your personal information is never shared with third parties, and payment processing is handled by trusted gateways — Stripe, PayPal and Razorpay." },
  ];
  return (
    <section style={{ padding: "80px 0", background: "white" }} aria-labelledby="faq-heading">
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px" }}>
        <header style={{ textAlign: "center", marginBottom: 48 }}>
          <h2 id="faq-heading" style={{ fontSize: "clamp(28px,4vw,42px)", color: "var(--navy)", marginBottom: 14 }}>Frequently Asked Questions</h2>
          <p style={{ color: "var(--g600)", fontSize: 17 }}>Everything you need to know about BioTutor AI</p>
        </header>
        <div role="list">
          {faqs.map((f, i) => (
            <div key={i} role="listitem" style={{ border: "1px solid var(--g200)", borderRadius: "var(--r-md)", marginBottom: 10, overflow: "hidden" }}>
              <button onClick={() => setOpen(open === i ? null : i)} aria-expanded={open === i} aria-controls={`faq-answer-${i}`}
                style={{ width: "100%", padding: "18px 20px", background: open === i ? "var(--off)" : "white", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 15, color: "var(--navy)", textAlign: "left", transition: "background .2s" }}>
                <span>{f.q}</span>
                <span style={{ fontSize: 20, color: "var(--orange)", flexShrink: 0, marginLeft: 12, transform: open === i ? "rotate(45deg)" : "none", transition: "transform .2s" }} aria-hidden="true">+</span>
              </button>
              {open === i && (
                <div id={`faq-answer-${i}`} style={{ padding: "0 20px 18px", color: "var(--g600)", fontSize: 15, lineHeight: 1.7, background: "var(--off)" }} role="region">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════
   TESTIMONIALS
══════════════════════════════════════════════ */
function Testimonials() {
  const items = [
    { name: "Priya Sharma", role: "NEET Aspirant, Delhi", text: "BioTutor AI helped me understand genetics and cell biology that I couldn't grasp from textbooks. Scored 95% in biology in my mock tests!", rating: 5, avatar: "PS" },
    { name: "James Wilson", role: "A-Level Student, UK", text: "Like having a personal biology tutor available 24/7. The explanations are so clear and the AI understands exactly what I'm asking.", rating: 5, avatar: "JW" },
    { name: "Ananya Patel", role: "Medical Student, Mumbai", text: "I use BioTutor AI to revise human anatomy before exams. The AI's explanations are detailed, accurate and perfectly exam-focused.", rating: 5, avatar: "AP" },
    { name: "Rahul Mehta", role: "CBSE Class 12, Ahmedabad", text: "All my doubts about photosynthesis and cellular respiration cleared in minutes. My biology marks improved from 72 to 91!", rating: 5, avatar: "RM" },
    { name: "Emma Chen", role: "Biology Teacher, Singapore", text: "I recommend BioTutor AI to all my students. It complements classroom teaching perfectly and helps students who need extra support.", rating: 5, avatar: "EC" },
    { name: "Arjun Singh", role: "JEE Advanced Aspirant, Pune", text: "Saves hours of searching through textbooks. Instant, accurate answers with examples. Best biology learning app I've used.", rating: 5, avatar: "AS" },
  ];
  return (
    <section style={{ padding: "80px 0", background: "var(--navy)" }} aria-labelledby="testimonials-heading">
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
        <header style={{ textAlign: "center", marginBottom: 48 }}>
          <h2 id="testimonials-heading" style={{ fontSize: "clamp(28px,4vw,42px)", color: "white", marginBottom: 14 }}>Students Love BioTutor AI</h2>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 17 }}>Join 50,000+ students already learning biology smarter with AI</p>
          <div style={{ display: "flex", gap: 4, justifyContent: "center", marginTop: 12 }} aria-label="Average rating: 4.9 out of 5">
            {Array(5).fill(0).map((_, i) => <Icon key={i} name="star" size={22} color="var(--orange)" />)}
            <span style={{ color: "white", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 16, marginLeft: 8 }}>4.9/5</span>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 14, marginLeft: 6, alignSelf: "center" }}>(2,847 reviews)</span>
          </div>
        </header>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 20 }} role="list">
          {items.map(t => (
            <article key={t.name} role="listitem" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "var(--r-md)", padding: 24, transition: "all .25s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,107,0,0.08)"; e.currentTarget.style.borderColor = "rgba(255,107,0,0.3)" }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)" }}>
              <div style={{ display: "flex", gap: 4, marginBottom: 12 }} aria-label={`${t.rating} stars`}>
                {Array(t.rating).fill(0).map((_, i) => <Icon key={i} name="star" size={14} color="var(--orange)" />)}
              </div>
              <blockquote style={{ color: "rgba(255,255,255,0.85)", fontSize: 14, lineHeight: 1.7, marginBottom: 16, fontStyle: "italic" }}>
                "{t.text}"
              </blockquote>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: "50%", background: "var(--grad-orange)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--ff-d)", fontWeight: 800, fontSize: 13, color: "white" }} aria-hidden="true">{t.avatar}</div>
                <div>
                  <cite style={{ color: "white", fontWeight: 700, fontSize: 14, fontStyle: "normal" }}>{t.name}</cite>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>{t.role}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════
   CTA SECTION
══════════════════════════════════════════════ */
function CTASection({ setPage }) {
  return (
    <section style={{ padding: "80px 24px", background: "white", textAlign: "center" }} id="about" aria-labelledby="cta-heading">
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <div style={{ fontSize: 60, marginBottom: 16 }} aria-hidden="true">🧬</div>
        <h2 id="cta-heading" style={{ fontSize: "clamp(28px,4vw,44px)", color: "var(--navy)", marginBottom: 16 }}>Ready to Master Biology?</h2>
        <p style={{ color: "var(--g600)", fontSize: 18, marginBottom: 36, lineHeight: 1.7 }}>
          Join 50,000+ students getting better grades with AI-powered biology tutoring. Start completely free — no credit card required. Get instant answers to every biology question.
        </p>
        <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => scrollTo("tutor")} aria-label="Start learning biology for free"
            style={{ padding: "15px 36px", background: "var(--grad-orange)", color: "white", border: "none", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 800, fontSize: 17, cursor: "pointer", boxShadow: "var(--sh-o)", transition: "transform .25s" }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-3px)"}
            onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
            Start Learning Free →
          </button>
          <button onClick={() => setPage("signup")} aria-label="Create a free account"
            style={{ padding: "15px 36px", background: "var(--navy)", color: "white", border: "none", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 17, cursor: "pointer", transition: "transform .25s" }}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-3px)"}
            onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
            Create Free Account
          </button>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════
   FOOTER
══════════════════════════════════════════════ */
function Footer({ setPage }) {
  return (
    <footer style={{ background: "var(--navy)", padding: "60px 24px 30px", color: "rgba(255,255,255,0.7)" }} role="contentinfo">
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 40, marginBottom: 48 }} className="mob-col">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, cursor: "pointer" }} onClick={() => setPage("home")} role="link" aria-label="BioTutor AI Home">
              <div style={{ width: 36, height: 36, background: "var(--grad-orange)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }} aria-hidden="true"><Icon name="dna" size={20} color="white" /></div>
              <span style={{ fontFamily: "var(--ff-d)", fontWeight: 900, fontSize: 20, color: "white" }}>Bio<span style={{ color: "var(--orange)" }}>Tutor</span> AI</span>
            </div>
            <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 280 }}>AI-powered biology tutoring for students worldwide. Get instant, accurate answers to all your biology questions. Perfect for NEET, CBSE, A-Level and international exams.</p>
            <div style={{ marginTop: 16, fontSize: 13 }}>
              <div>📧 support@biotutor.ai</div>
            </div>
          </div>
          {[
            { title: "Product", links: [["Ask AI Tutor", "tutor"], ["Biology Topics", "topics"], ["Pricing Plans", "pricing"], ["Student Dashboard", "dashboard"]] },
            { title: "Company", links: [["About Us", "about"], ["Blog", "blog"], ["Contact", "contact"], ["Privacy Policy", "privacy"]] },
            { title: "Support", links: [["Help Center", "help"], ["Terms of Use", "terms"], ["Refund Policy", "refund"], ["Security", "security"]] },
          ].map(col => (
            <nav key={col.title} aria-label={`${col.title} links`}>
              <div style={{ color: "white", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 14, marginBottom: 16, textTransform: "uppercase", letterSpacing: 0.8 }}>{col.title}</div>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
                {col.links.map(([label, id]) => (
                  <li key={label}><span style={{ cursor: "pointer", fontSize: 14, transition: "color .2s" }} role="link" tabIndex={0}
                    onClick={() => scrollTo(id)} onKeyDown={e => e.key === "Enter" && scrollTo(id)}
                    onMouseEnter={e => e.target.style.color = "var(--orange)"}
                    onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.7)"}>{label}</span></li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <span style={{ fontSize: 13 }}>© {new Date().getFullYear()} BioTutor AI. All rights reserved. | <span style={{ cursor: "pointer" }} onClick={() => scrollTo("privacy")}>Privacy Policy</span> | <span style={{ cursor: "pointer" }} onClick={() => scrollTo("terms")}>Terms of Service</span></span>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: 13 }}>SSL Secured 🔒</span>
            <button
              onClick={() => setPage("admin")}
              style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.5)", padding: "6px 14px", borderRadius: "var(--r-sm)", cursor: "pointer", fontSize: 12, fontFamily: "var(--ff-d)", fontWeight: 600, transition: "all .2s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,107,0,0.2)"; e.currentTarget.style.color = "var(--orange)"; e.currentTarget.style.borderColor = "var(--orange)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; }}
              aria-label="Admin Panel">
              ⚙️ Admin
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ══════════════════════════════════════════════
   AUTH PAGES — with reCAPTCHA v3
══════════════════════════════════════════════ */
function AuthPage({ mode, setPage, onAuth }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const isLogin = mode === "login";
  useEffect(() => { loadRecaptcha(); }, []);

  const submit = async () => {
    setErr("");
    if (!form.email || !form.password) { setErr("Please fill all required fields"); return; }
    if (!isLogin && form.password !== form.confirm) { setErr("Passwords don't match"); return; }
    if (!isLogin && form.password.length < 8) { setErr("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      const captchaToken = await getRecaptchaToken(isLogin ? "login" : "register");
      const res = await fetch(`${API_BASE}/auth/${isLogin ? "login" : "register"}.php`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, password: form.password, captcha: captchaToken })
      });
      const data = await res.json();
      if (data.success) { onAuth(data.user); toast(`Welcome${isLogin ? " back" : ""}!`, "ok"); setPage("dashboard"); }
      else setErr(data.message || "Authentication failed. Please try again.");
    } catch { setErr("Network error. Please check your connection and try again."); }
    finally { setLoading(false); }
  };

  const inputStyle = { width: "100%", padding: "12px 16px", border: "2px solid var(--g200)", borderRadius: "var(--r-sm)", fontFamily: "var(--ff-b)", fontSize: 15 };
  return (
    <main style={{ minHeight: "100vh", background: "var(--grad-hero)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} id="main-content">
      <div style={{ background: "white", borderRadius: "var(--r-lg)", padding: 40, width: "100%", maxWidth: 420, boxShadow: "var(--sh-lg)" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ width: 54, height: 54, background: "var(--grad-orange)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }} aria-hidden="true">
            <Icon name="dna" size={26} color="white" />
          </div>
          <h1 style={{ color: "var(--navy)", marginBottom: 8, fontSize: 24 }}>{isLogin ? "Welcome Back" : "Create Free Account"}</h1>
          <p style={{ color: "var(--g600)", fontSize: 14 }}>{isLogin ? "Log in to continue your biology journey" : "Join 50,000+ students learning biology with AI"}</p>
        </div>
        {err && <div role="alert" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "var(--r-sm)", padding: "10px 14px", color: "var(--red)", fontSize: 14, marginBottom: 16 }}>{err}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }} role="form" aria-label={isLogin ? "Login form" : "Registration form"}>
          {!isLogin && (
            <div>
              <label htmlFor="auth-name">Full Name <span aria-hidden="true" style={{ color: "var(--red)" }}>*</span></label>
              <input id="auth-name" type="text" placeholder="John Doe" autoComplete="name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} required />
            </div>
          )}
          <div>
            <label htmlFor="auth-email">Email Address <span aria-hidden="true" style={{ color: "var(--red)" }}>*</span></label>
            <input id="auth-email" type="email" placeholder="you@example.com" autoComplete="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} required />
          </div>
          <div>
            <label htmlFor="auth-password">Password <span aria-hidden="true" style={{ color: "var(--red)" }}>*</span></label>
            <input id="auth-password" type="password" placeholder="••••••••" autoComplete={isLogin ? "current-password" : "new-password"} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} style={inputStyle} required minLength={8} />
          </div>
          {!isLogin && (
            <div>
              <label htmlFor="auth-confirm">Confirm Password <span aria-hidden="true" style={{ color: "var(--red)" }}>*</span></label>
              <input id="auth-confirm" type="password" placeholder="••••••••" autoComplete="new-password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} style={inputStyle} required />
            </div>
          )}
        </div>
        <p style={{ fontSize: 12, color: "var(--g600)", marginTop: 12, textAlign: "center" }}>
          Protected by reCAPTCHA. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "var(--orange)" }}>Privacy</a> &amp; <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: "var(--orange)" }}>Terms</a> apply.
        </p>
        <button onClick={submit} disabled={loading} aria-busy={loading}
          style={{ width: "100%", marginTop: 16, padding: "14px", background: "var(--grad-orange)", color: "white", border: "none", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 16, cursor: loading ? "not-allowed" : "pointer", boxShadow: "var(--sh-o)", transition: "all .25s", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: loading ? 0.8 : 1 }}>
          {loading ? <><div className="spin" style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%" }} /><span>Processing...</span></> : isLogin ? "Log In to BioTutor →" : "Create Free Account →"}
        </button>
        <div style={{ textAlign: "center", marginTop: 20, fontSize: 14, color: "var(--g600)" }}>
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <span style={{ color: "var(--orange)", fontWeight: 700, cursor: "pointer" }} role="link" tabIndex={0}
            onClick={() => setPage(isLogin ? "signup" : "login")}
            onKeyDown={e => e.key === "Enter" && setPage(isLogin ? "signup" : "login")}>
            {isLogin ? "Sign Up Free" : "Log In"}
          </span>
        </div>
        <button onClick={() => setPage("home")} style={{ display: "block", margin: "16px auto 0", background: "none", border: "none", color: "var(--g400)", cursor: "pointer", fontSize: 13 }}>← Back to Home</button>
      </div>
    </main>
  );
}

/* ══════════════════════════════════════════════
   CHECKOUT — with reCAPTCHA
══════════════════════════════════════════════ */
function CheckoutPage({ setPage }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [plan, setPlan] = useState("pro");
  const [gateway, setGateway] = useState("stripe");
  const [loading, setLoading] = useState(false);
  const selected = PLANS.find(p => p.id === plan) || PLANS[1];
  useEffect(() => { loadRecaptcha(); }, []);

  const pay = async () => {
    setLoading(true);
    try {
      const captchaToken = await getRecaptchaToken("checkout");
      const res = await fetch(`${API_BASE}/payments/create.php`, {
        method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${user?.token}` },
        body: JSON.stringify({ planId: plan, gateway, userId: user?.id, captcha: captchaToken })
      });
      const data = await res.json();
      if (data.redirect) window.location.href = data.redirect;
      else toast(data.message || "Payment initiated", "ok");
    } catch { toast("Payment error. Please try again.", "err"); }
    finally { setLoading(false); }
  };

  return (
    <main style={{ minHeight: "100vh", background: "var(--off)", paddingTop: 100, paddingBottom: 60 }} id="main-content" aria-labelledby="checkout-heading">
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px", display: "grid", gridTemplateColumns: "1fr 380px", gap: 32 }} className="mob-col">
        <div>
          <h1 id="checkout-heading" style={{ color: "var(--navy)", marginBottom: 8, fontSize: 28 }}>Complete Your Purchase</h1>
          <p style={{ color: "var(--g600)", marginBottom: 28 }}>Choose your plan and secure payment method</p>
          <div style={{ marginBottom: 28 }}>
            <p style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 12 }}>Select Plan</p>
            {PLANS.filter(p => p.id !== "free").map(p => (
              <div key={p.id} onClick={() => setPlan(p.id)} role="radio" aria-checked={plan === p.id} tabIndex={0}
                onKeyDown={e => e.key === "Enter" && setPlan(p.id)}
                style={{ border: plan === p.id ? "2px solid var(--orange)" : "2px solid var(--g200)", borderRadius: "var(--r-md)", padding: "16px 20px", marginBottom: 10, cursor: "pointer", background: "white", transition: "all .2s", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontFamily: "var(--ff-d)", fontWeight: 700, color: "var(--navy)" }}>{p.name}</div>
                  <div style={{ fontSize: 13, color: "var(--g600)" }}>{p.features.slice(0, 3).join(", ")}</div>
                </div>
                <div style={{ fontFamily: "var(--ff-d)", fontWeight: 900, color: "var(--orange)", fontSize: 20 }}>${p.price}/mo</div>
              </div>
            ))}
          </div>
          <div>
            <p style={{ fontWeight: 700, color: "var(--navy)", marginBottom: 12 }}>Payment Method</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }} role="radiogroup" aria-label="Payment method">
              {[{ id: "stripe", name: "Stripe", icon: "💳" }, { id: "paypal", name: "PayPal", icon: "🅿️" }, { id: "razorpay", name: "Razorpay", icon: "₹" }].map(g => (
                <div key={g.id} onClick={() => setGateway(g.id)} role="radio" aria-checked={gateway === g.id} tabIndex={0}
                  onKeyDown={e => e.key === "Enter" && setGateway(g.id)}
                  style={{ border: gateway === g.id ? "2px solid var(--orange)" : "2px solid var(--g200)", borderRadius: "var(--r-md)", padding: 16, cursor: "pointer", background: "white", textAlign: "center", transition: "all .2s" }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }} aria-hidden="true">{g.icon}</div>
                  <div style={{ fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 13, color: gateway === g.id ? "var(--orange)" : "var(--navy)" }}>{g.name}</div>
                </div>
              ))}
            </div>
          </div>
          <p style={{ fontSize: 12, color: "var(--g600)", marginTop: 16, textAlign: "center" }}>
            Protected by reCAPTCHA. SSL encrypted. All payments are secure.
          </p>
        </div>
        <div>
          <div style={{ background: "white", borderRadius: "var(--r-lg)", padding: 28, boxShadow: "var(--sh-md)", position: "sticky", top: 100 }} role="region" aria-label="Order summary">
            <h2 style={{ color: "var(--navy)", marginBottom: 20, fontFamily: "var(--ff-d)", fontSize: 20 }}>Order Summary</h2>
            <dl style={{ borderBottom: "1px solid var(--g200)", paddingBottom: 16, marginBottom: 16 }}>
              {[["Plan", selected.name], ["Billing", "Monthly"], ["Gateway", gateway.charAt(0).toUpperCase() + gateway.slice(1)]].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <dt style={{ color: "var(--g600)", fontSize: 14 }}>{k}</dt>
                  <dd style={{ fontWeight: 700, color: "var(--navy)", textTransform: "capitalize" }}>{v}</dd>
                </div>
              ))}
            </dl>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
              <span style={{ fontFamily: "var(--ff-d)", fontWeight: 700, color: "var(--navy)" }}>Total</span>
              <span style={{ fontFamily: "var(--ff-d)", fontWeight: 900, fontSize: 22, color: "var(--orange)" }} aria-label={`Total: $${selected.price} per month`}>${selected.price}/mo</span>
            </div>
            <button onClick={pay} disabled={loading} aria-busy={loading}
              style={{ width: "100%", padding: "14px", background: "var(--grad-orange)", color: "white", border: "none", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 16, cursor: loading ? "not-allowed" : "pointer", boxShadow: "var(--sh-o)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: loading ? 0.8 : 1 }}>
              {loading ? <><div className="spin" style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%" }} />Processing...</> : `Pay with ${gateway.charAt(0).toUpperCase() + gateway.slice(1)} →`}
            </button>
            <p style={{ textAlign: "center", marginTop: 14, fontSize: 12, color: "var(--g600)", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
              <Icon name="shield" size={12} color="var(--green)" /> 30-day money-back guarantee
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}

/* ══════════════════════════════════════════════
   USER DASHBOARD
══════════════════════════════════════════════ */
function Dashboard({ setPage }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [history] = useState([
    { id: 1, question: "What is the difference between mitosis and meiosis?", date: "2024-01-15", topic: "Cell Biology" },
    { id: 2, question: "Explain the structure of DNA double helix", date: "2024-01-14", topic: "Genetics" },
    { id: 3, question: "How does the human immune system work?", date: "2024-01-13", topic: "Immunology" },
  ]);
  const navItems = [
    { id: "overview", label: "Overview", emoji: "📊" },
    { id: "history", label: "Chat History", emoji: "💬" },
    { id: "settings", label: "Settings", emoji: "⚙️" },
    { id: "billing", label: "Billing", emoji: "💳" },
  ];
  return (
    <main style={{ minHeight: "100vh", background: "var(--off)", paddingTop: 80 }} id="main-content">
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px", display: "grid", gridTemplateColumns: "220px 1fr", gap: 28, alignItems: "start" }} className="mob-col">
        <nav style={{ background: "white", borderRadius: "var(--r-lg)", padding: 20, boxShadow: "var(--sh-sm)", border: "1px solid var(--g200)" }} aria-label="Dashboard navigation">
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid var(--g200)" }}>
            <div style={{ width: 44, height: 44, background: "var(--grad-orange)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--ff-d)", fontWeight: 900, color: "white", fontSize: 16 }} aria-hidden="true">
              {user?.name?.charAt(0) || "U"}
            </div>
            <div>
              <div style={{ fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 14, color: "var(--navy)" }}>{user?.name || "Student"}</div>
              <div style={{ fontSize: 12, color: "var(--g600)" }}>{user?.plan || "Free"} Plan</div>
            </div>
          </div>
          {navItems.map(n => (
            <button key={n.id} onClick={() => setTab(n.id)} aria-current={tab === n.id ? "page" : undefined}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: "var(--r-sm)", border: "none", cursor: "pointer", marginBottom: 4, background: tab === n.id ? "rgba(255,107,0,0.1)" : "transparent", color: tab === n.id ? "var(--orange)" : "var(--g600)", fontFamily: "var(--ff-d)", fontWeight: tab === n.id ? 700 : 600, fontSize: 14, textAlign: "left", transition: "all .2s" }}>
              {n.emoji} {n.label}
            </button>
          ))}
          <div style={{ borderTop: "1px solid var(--g200)", paddingTop: 16, marginTop: 12 }}>
            <button onClick={() => setPage("home")} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: "var(--r-sm)", border: "none", cursor: "pointer", background: "transparent", color: "var(--red)", fontFamily: "var(--ff-d)", fontWeight: 600, fontSize: 14, textAlign: "left" }}>
              ← Back to Home
            </button>
          </div>
        </nav>
        <div>
          {tab === "overview" && (
            <section aria-labelledby="overview-heading">
              <h1 id="overview-heading" style={{ color: "var(--navy)", marginBottom: 24, fontSize: 26 }}>Welcome back, {user?.name?.split(" ")[0] || "Student"}! 👋</h1>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16, marginBottom: 28 }} role="list">
                {[["Questions Asked", "47", "var(--orange)"], ["Topics Explored", "12", "var(--navy)"], ["Day Streak", "7 days", "var(--green)"], ["Current Plan", user?.plan || "Free", "var(--g600)"]].map(([label, val, color]) => (
                  <div key={label} role="listitem" style={{ background: "white", borderRadius: "var(--r-md)", padding: 20, boxShadow: "var(--sh-sm)", border: "1px solid var(--g200)" }}>
                    <div style={{ fontSize: 24, fontFamily: "var(--ff-d)", fontWeight: 900, color }}>{val}</div>
                    <div style={{ fontSize: 13, color: "var(--g600)", marginTop: 4 }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: "white", borderRadius: "var(--r-lg)", padding: 24, boxShadow: "var(--sh-sm)", border: "1px solid var(--g200)", marginBottom: 20 }}>
                <h2 style={{ color: "var(--navy)", marginBottom: 16, fontFamily: "var(--ff-d)", fontSize: 18 }}>Quick Actions</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }}>
                  <button onClick={() => setPage("home")} style={{ padding: "14px", background: "var(--grad-orange)", color: "white", border: "none", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 700, cursor: "pointer" }}>🧬 Ask AI</button>
                  <button onClick={() => setTab("history")} style={{ padding: "14px", background: "var(--navy)", color: "white", border: "none", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 700, cursor: "pointer" }}>💬 View History</button>
                  <button onClick={() => setTab("billing")} style={{ padding: "14px", background: "var(--g100)", color: "var(--navy)", border: "1px solid var(--g200)", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 700, cursor: "pointer" }}>💳 Upgrade Plan</button>
                </div>
              </div>
              <div style={{ background: "linear-gradient(135deg,var(--navy),var(--navy-light))", borderRadius: "var(--r-lg)", padding: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
                <div>
                  <div style={{ color: "white", fontFamily: "var(--ff-d)", fontWeight: 800, fontSize: 18 }}>Upgrade to Pro Plan</div>
                  <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>Get unlimited questions, save history & all premium features</div>
                </div>
                <button onClick={() => setPage("checkout")} style={{ padding: "12px 24px", background: "var(--grad-orange)", color: "white", border: "none", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 700, cursor: "pointer", boxShadow: "var(--sh-o)", whiteSpace: "nowrap" }}>Upgrade Now →</button>
              </div>
            </section>
          )}
          {tab === "history" && (
            <section aria-labelledby="history-heading">
              <h1 id="history-heading" style={{ color: "var(--navy)", marginBottom: 24, fontSize: 26 }}>Chat History</h1>
              <div style={{ background: "white", borderRadius: "var(--r-lg)", boxShadow: "var(--sh-sm)", border: "1px solid var(--g200)", overflow: "hidden" }} role="list">
                {history.map((h, i) => (
                  <article key={h.id} role="listitem" style={{ padding: "18px 24px", borderBottom: i < history.length - 1 ? "1px solid var(--g200)" : "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div>
                      <p style={{ fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>{h.question}</p>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <span style={{ fontSize: 12, background: "rgba(255,107,0,0.1)", color: "var(--orange)", borderRadius: 99, padding: "2px 8px", fontWeight: 600 }}>{h.topic}</span>
                        <time style={{ fontSize: 12, color: "var(--g600)" }} dateTime={h.date}>{fmtDate(h.date)}</time>
                      </div>
                    </div>
                    <button style={{ padding: "7px 16px", background: "var(--g100)", border: "none", borderRadius: "var(--r-sm)", fontFamily: "var(--ff-d)", fontWeight: 600, fontSize: 13, cursor: "pointer", color: "var(--navy)", whiteSpace: "nowrap" }} aria-label={`View answer for: ${h.question}`}>View →</button>
                  </article>
                ))}
              </div>
            </section>
          )}
          {tab === "billing" && (
            <section aria-labelledby="billing-heading">
              <h1 id="billing-heading" style={{ color: "var(--navy)", marginBottom: 24, fontSize: 26 }}>Billing & Subscription</h1>
              <div style={{ background: "white", borderRadius: "var(--r-lg)", padding: 28, boxShadow: "var(--sh-sm)", border: "1px solid var(--g200)", marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                  <div>
                    <h2 style={{ fontFamily: "var(--ff-d)", fontWeight: 700, color: "var(--navy)", fontSize: 18 }}>Current Plan: {user?.plan || "Free"}</h2>
                    <p style={{ color: "var(--g600)", fontSize: 14 }}>Manage your subscription and billing details</p>
                  </div>
                  <span style={{ background: "rgba(255,107,0,0.1)", color: "var(--orange)", borderRadius: 99, padding: "5px 14px", fontWeight: 700, fontSize: 13, fontFamily: "var(--ff-d)" }}>{user?.plan || "Free"}</span>
                </div>
                <button onClick={() => setPage("checkout")} style={{ padding: "12px 24px", background: "var(--grad-orange)", color: "white", border: "none", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 700, cursor: "pointer", boxShadow: "var(--sh-o)" }}>Upgrade Plan →</button>
              </div>
              <div style={{ background: "white", borderRadius: "var(--r-lg)", padding: 28, boxShadow: "var(--sh-sm)", border: "1px solid var(--g200)" }}>
                <h2 style={{ color: "var(--navy)", marginBottom: 16, fontSize: 16 }}>Accepted Payment Methods</h2>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {["💳 Stripe", "🅿️ PayPal", "₹ Razorpay"].map(g => (
                    <div key={g} style={{ padding: "10px 18px", border: "1px solid var(--g200)", borderRadius: "var(--r-sm)", fontSize: 14, fontWeight: 600, color: "var(--navy)" }}>{g}</div>
                  ))}
                </div>
              </div>
            </section>
          )}
          {tab === "settings" && (
            <section aria-labelledby="settings-heading">
              <h1 id="settings-heading" style={{ color: "var(--navy)", marginBottom: 24, fontSize: 26 }}>Account Settings</h1>
              <div style={{ background: "white", borderRadius: "var(--r-lg)", padding: 28, boxShadow: "var(--sh-sm)", border: "1px solid var(--g200)" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div><label htmlFor="s-name">Display Name</label><input id="s-name" type="text" defaultValue={user?.name || ""} autoComplete="name" /></div>
                  <div><label htmlFor="s-email">Email Address</label><input id="s-email" type="email" defaultValue={user?.email || ""} autoComplete="email" /></div>
                  <div><label htmlFor="s-pass">New Password</label><input id="s-pass" type="password" placeholder="Leave blank to keep current password" autoComplete="new-password" /></div>
                  <div style={{ borderTop: "1px solid var(--g200)", paddingTop: 16 }}>
                    <button style={{ padding: "12px 28px", background: "var(--grad-orange)", color: "white", border: "none", borderRadius: "var(--r-md)", fontFamily: "var(--ff-d)", fontWeight: 700, cursor: "pointer", boxShadow: "var(--sh-o)" }}>Save Changes</button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </main>
  );
}


/* ══════════════════════════════════════════════
   ADMIN LOGIN PAGE  — math captcha, no reCAPTCHA needed
══════════════════════════════════════════════ */
function makeMathQ() {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  return { q: `${a} + ${b}`, ans: a + b };
}
function AdminLogin({ onAdminAuth }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [math, setMath] = useState(() => makeMathQ());
  const [mathAns, setMathAns] = useState("");

  const refreshMath = () => { setMath(makeMathQ()); setMathAns(""); };

  const submit = async () => {
    setErr("");
    if (!form.email || !form.password) { setErr("Please enter your email and password"); return; }
    if (parseInt(mathAns, 10) !== math.ans) { setErr("Incorrect answer to the security question"); refreshMath(); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/login.php`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, password: form.password })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem("adminToken", data.token);
        onAdminAuth(data.admin);
        toast("Welcome to Admin Panel", "ok");
      } else {
        setErr(data.message || "Invalid credentials. Please try again.");
        refreshMath();
      }
    } catch {
      setErr("Cannot reach server. Check that your PHP API is running and API_BASE is correct.");
      refreshMath();
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--grad-hero)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 24px", fontFamily: "var(--ff-b)", overflowY: "auto" }}>
      <div style={{ width: "100%", maxWidth: 440 }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 56, height: 56, background: "var(--grad-orange)", borderRadius: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12, boxShadow: "var(--sh-o)" }}>
            <Icon name="dna" size={28} color="white" />
          </div>
          <div style={{ color: "white", fontFamily: "var(--ff-d)", fontWeight: 900, fontSize: 22 }}>BioTutor <span style={{ color: "var(--orange)" }}>Admin</span></div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, marginTop: 4 }}>Secure Admin Access</div>
        </div>

        {/* Card */}
        <div style={{ background: "white", borderRadius: 16, padding: "28px 28px 24px", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>

          {/* Error */}
          {err && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", color: "#DC2626", fontSize: 13, marginBottom: 16, fontWeight: 500 }}>
              ⚠️ {err}
            </div>
          )}

          {/* Email */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontWeight: 700, fontSize: 13, color: "#0A1628", marginBottom: 6 }}>Admin Email</label>
            <input
              type="email" placeholder="admin@abiology.in"
              value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && submit()} autoComplete="email"
              style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #E9ECEF", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box" }}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontWeight: 700, fontSize: 13, color: "#0A1628", marginBottom: 6 }}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPass ? "text" : "password"} placeholder="••••••••"
                value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                onKeyDown={e => e.key === "Enter" && submit()} autoComplete="current-password"
                style={{ width: "100%", padding: "10px 44px 10px 14px", border: "1.5px solid #E9ECEF", borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box" }}
              />
              <button type="button" onClick={() => setShowPass(v => !v)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#6C757D", padding: "4px" }}>
                {showPass ? "HIDE" : "SHOW"}
              </button>
            </div>
          </div>

          {/* Math captcha */}
          <div style={{ background: "#FFF8F3", border: "1.5px solid #FF6B00", borderRadius: 8, padding: "14px", marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0A1628", marginBottom: 10 }}>
              🔒 Security Check
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#FF6B00", textAlign: "center", marginBottom: 10, letterSpacing: 2 }}>
              {math.q} = ?
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text" inputMode="numeric" pattern="[0-9]*"
                placeholder="Type your answer here"
                value={mathAns} onChange={e => setMathAns(e.target.value.replace(/[^0-9]/g, ""))}
                onKeyDown={e => e.key === "Enter" && submit()}
                style={{ flex: 1, padding: "10px 14px", border: "1.5px solid #E9ECEF", borderRadius: 8, fontSize: 16, fontWeight: 700, textAlign: "center", outline: "none", boxSizing: "border-box" }}
              />
              <button type="button" onClick={refreshMath} title="New question"
                style={{ padding: "10px 14px", background: "#F1F3F5", border: "1px solid #E9ECEF", borderRadius: 8, cursor: "pointer", fontSize: 16 }}>
                🔄
              </button>
            </div>
          </div>

          {/* SIGN IN BUTTON */}
          <button
            onClick={submit}
            disabled={loading}
            style={{
              display: "block",
              width: "100%",
              padding: "14px",
              background: loading ? "#adb5bd" : "#FF6B00",
              color: "white",
              border: "none",
              borderRadius: 8,
              fontFamily: "Montserrat,sans-serif",
              fontWeight: 900,
              fontSize: 16,
              cursor: loading ? "not-allowed" : "pointer",
              marginBottom: 12,
              letterSpacing: 0.5
            }}>
            {loading ? "Signing In..." : "Sign In to Admin →"}
          </button>

          <div style={{ fontSize: 11, color: "#6C757D", textAlign: "center", padding: "8px 0" }}>
            🔐 All admin activity is logged. Unauthorised access is prohibited.
          </div>
        </div>

      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   ADMIN PANEL — improved UI + all save buttons
══════════════════════════════════════════════ */
function AdminPanel({ setPage, adminUser, onAdminLogout }) {
  const { toast } = useToast();
  const [adminTab, setAdminTab] = useState("overview");
  const [savingSection, setSavingSection] = useState("");
  const [showKeys, setShowKeys] = useState({});

  const [seoSettings, setSeoSettings] = useState({
    siteName: "BioTutor AI", siteUrl: "https://yourdomain.com",
    metaTitle: "BioTutor AI – Free AI Biology Tutor for NEET, CBSE, A-Level Students",
    metaDesc: "Get instant AI-powered biology answers for NEET, CBSE, ICSE, A-Level & more.",
    metaKeywords: "biology tutor, ai biology tutor, neet biology, cbse biology, biology questions answers",
    googleAnalyticsId: "", gtmId: "", googleSearchConsole: "",
    robotsTxt: "User-agent: *\nAllow: /\nDisallow: /admin\nDisallow: /api\nSitemap: https://yourdomain.com/sitemap.xml",
    sitemapFrequency: "weekly"
  });

  const [apiSettings, setApiSettings] = useState({
    openaiApiKey: "", openaiModel: "gpt-4o-mini",
    apiEndpoint: "https://api.openai.com/v1/chat/completions",
    freeQuestionsPerDay: "5", maxTokens: "1000", temperature: "0.7",
    systemPrompt: "You are an expert biology tutor. Explain biology concepts clearly, accurately and in a student-friendly way. Use bullet points and structured formatting."
  });

  const [paySettings, setPaySettings] = useState({
    stripeSecretKey: "", stripeWebhookSecret: "", stripeMode: "live",
    paypalClientId: "", paypalClientSecret: "", paypalMode: "live",
    razorpayKeyId: "", razorpayKeySecret: ""
  });

  const [pricingSettings, setPricingSettings] = useState({
    freePrice: "0", freeDailyLimit: "5",
    proMonthly: "9.99", proYearly: "83.88",
    premiumMonthly: "19.99", premiumYearly: "167.88"
  });

  const [secForm, setSecForm] = useState({ curPass: "", newPass: "", confPass: "" });
  const [newAdmin, setNewAdmin] = useState({ name: "", email: "", password: "", role: "Super Admin" });

  const adminNav = [
    { id: "overview", label: "Dashboard", emoji: "📊" },
    { id: "users", label: "Users", emoji: "👥" },
    { id: "pricing", label: "Pricing", emoji: "💰" },
    { id: "payments", label: "Payments", emoji: "💳" },
    { id: "api", label: "API Settings", emoji: "🔌" },
    { id: "seo", label: "SEO Settings", emoji: "🔍" },
    { id: "security", label: "Security", emoji: "🔐" },
  ];

  const mockStats = { totalUsers: 1247, activeToday: 89, totalQuestions: 45231, revenue: 12840, proUsers: 342, premiumUsers: 87, revenueMonth: 2840 };

  /* ── API save helper ── */
  const saveSection = async (section, payload, label) => {
    setSavingSection(section);
    try {
      const token = localStorage.getItem("adminToken") || "";
      const res = await fetch(`${API_BASE}/admin/settings.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success || res.status === 200) toast(`${label} saved successfully ✓`, "ok");
      else toast(data.message || "Save failed. Check server connection.", "err");
    } catch {
      toast(`${label} saved (offline mode — connect PHP backend to persist)`, "ok");
    } finally { setSavingSection(""); }
  };

  /* ── Change password ── */
  const savePassword = async () => {
    if (!secForm.curPass || !secForm.newPass) { toast("Fill all password fields", "err"); return; }
    if (secForm.newPass !== secForm.confPass) { toast("New passwords do not match", "err"); return; }
    if (secForm.newPass.length < 8) { toast("Min 8 characters required", "err"); return; }
    setSavingSection("password");
    try {
      const token = localStorage.getItem("adminToken") || "";
      await fetch(`${API_BASE}/admin/change-password.php`, {
        method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: secForm.curPass, newPassword: secForm.newPass })
      });
      toast("Password updated successfully ✓", "ok");
      setSecForm({ curPass: "", newPass: "", confPass: "" });
    } catch { toast("Password updated (offline mode)", "ok"); }
    finally { setSavingSection(""); }
  };

  /* ── Create new admin ── */
  const createAdmin = async () => {
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) { toast("Fill all admin fields", "err"); return; }
    setSavingSection("newadmin");
    try {
      const token = localStorage.getItem("adminToken") || "";
      await fetch(`${API_BASE}/admin/create-admin.php`, {
        method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(newAdmin)
      });
      toast(`Admin account created for ${newAdmin.email} ✓`, "ok");
      setNewAdmin({ name: "", email: "", password: "", role: "Super Admin" });
    } catch { toast("Admin created (offline mode)", "ok"); }
    finally { setSavingSection(""); }
  };

  const toggleKey = (k) => setShowKeys(v => ({ ...v, [k]: !v[k] }));

  /* ── Reusable components ── */
  const SaveBtn = ({ section, label = "Save Changes", onClick }) => (
    <button disabled={savingSection === section} onClick={onClick}
      style={{ padding: "11px 24px", background: savingSection === section ? "var(--g400)" : "var(--grad-orange)", color: "white", border: "none", borderRadius: "var(--r-sm)", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 14, cursor: savingSection === section ? "not-allowed" : "pointer", boxShadow: savingSection === section ? "none" : "var(--sh-o)", display: "inline-flex", alignItems: "center", gap: 8, transition: "all .2s", minWidth: 160, justifyContent: "center" }}>
      {savingSection === section
        ? <><div className="spin" style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%" }} />Saving...</>
        : `✓ ${label}`}
    </button>
  );

  const SecBtn = ({ onClick, label, loading: l }) => (
    <button onClick={onClick} disabled={l}
      style={{ padding: "11px 24px", background: "var(--navy)", color: "white", border: "none", borderRadius: "var(--r-sm)", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 14, cursor: l ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 8, minWidth: 140, justifyContent: "center", opacity: l ? 0.7 : 1 }}>
      {l ? <><div className="spin" style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%" }} />Processing...</> : label}
    </button>
  );

  const Card = ({ title, icon, children, footer }) => (
    <div style={{ background: "white", borderRadius: "var(--r-lg)", boxShadow: "var(--sh-sm)", border: "1px solid var(--g200)", marginBottom: 20, overflow: "hidden" }}>
      <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--g100)", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        <h2 style={{ color: "var(--navy)", fontFamily: "var(--ff-d)", fontSize: 16, fontWeight: 800 }}>{title}</h2>
      </div>
      <div style={{ padding: "22px 24px" }}>{children}</div>
      {footer && <div style={{ padding: "16px 24px", borderTop: "1px solid var(--g100)", background: "var(--off)", display: "flex", justifyContent: "flex-end", gap: 10 }}>{footer}</div>}
    </div>
  );

  const KeyField = ({ id, label, stateKey, placeholder, value, onChange, hint }) => (
    <div>
      <label htmlFor={id}>{label}</label>
      <div style={{ position: "relative" }}>
        <input id={id} type={showKeys[stateKey] ? "text" : "password"} placeholder={placeholder}
          value={value} onChange={onChange} autoComplete="off" style={{ paddingRight: 68 }} />
        <button type="button" onClick={() => toggleKey(stateKey)}
          style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "var(--g100)", border: "1px solid var(--g200)", borderRadius: 4, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "var(--g600)", fontFamily: "var(--ff-d)", whiteSpace: "nowrap" }}>
          {showKeys[stateKey] ? "HIDE" : "SHOW"}
        </button>
      </div>
      {hint && <p style={{ fontSize: 11, color: "var(--g600)", marginTop: 4 }}>💡 {hint}</p>}
    </div>
  );

  const InfoBox = ({ children, type = "info" }) => (
    <div style={{ background: type === "info" ? "rgba(255,107,0,0.05)" : type === "success" ? "rgba(0,200,83,0.06)" : "rgba(10,22,40,0.04)", border: `1px solid ${type === "info" ? "rgba(255,107,0,0.2)" : type === "success" ? "rgba(0,200,83,0.2)" : "rgba(10,22,40,0.1)"}`, borderRadius: "var(--r-sm)", padding: "11px 16px", fontSize: 13, color: "var(--navy)", marginBottom: 18 }}>
      {children}
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#F0F2F5", fontFamily: "var(--ff-b)" }}>
      {/* Top Bar */}
      <div style={{ background: "var(--navy)", padding: "0 24px", height: 62, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 12px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 34, height: 34, background: "var(--grad-orange)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="dna" size={18} color="white" />
          </div>
          <div>
            <span style={{ color: "white", fontFamily: "var(--ff-d)", fontWeight: 900, fontSize: 16 }}>BioTutor <span style={{ color: "var(--orange)" }}>Admin</span></span>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, marginLeft: 10 }}>{adminUser?.role || "Admin"}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={() => setPage("home")} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.8)", padding: "7px 16px", borderRadius: "var(--r-sm)", cursor: "pointer", fontFamily: "var(--ff-d)", fontWeight: 600, fontSize: 13, transition: "all .2s" }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}>
            ↗ View Site
          </button>
          <button onClick={onAdminLogout} style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.25)", color: "#fca5a5", padding: "7px 16px", borderRadius: "var(--r-sm)", cursor: "pointer", fontFamily: "var(--ff-d)", fontWeight: 600, fontSize: 13 }}>
            Sign Out
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", minHeight: "calc(100vh - 62px)" }}>
        {/* Sidebar */}
        <nav style={{ background: "white", borderRight: "1px solid var(--g200)", padding: "20px 12px", position: "sticky", top: 62, height: "calc(100vh - 62px)", overflowY: "auto" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--g400)", letterSpacing: 1.5, textTransform: "uppercase", padding: "0 10px", marginBottom: 10, fontFamily: "var(--ff-d)" }}>Navigation</div>
          {adminNav.map(n => (
            <button key={n.id} onClick={() => setAdminTab(n.id)}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: "var(--r-sm)", border: "none", cursor: "pointer", marginBottom: 2, background: adminTab === n.id ? "rgba(255,107,0,0.1)" : "transparent", color: adminTab === n.id ? "var(--orange)" : "var(--g600)", fontFamily: "var(--ff-d)", fontWeight: adminTab === n.id ? 700 : 600, fontSize: 14, textAlign: "left", transition: "all .15s" }}>
              <span style={{ fontSize: 16 }}>{n.emoji}</span> {n.label}
              {adminTab === n.id && <span style={{ marginLeft: "auto", width: 6, height: 6, background: "var(--orange)", borderRadius: "50%" }} />}
            </button>
          ))}
          <div style={{ borderTop: "1px solid var(--g200)", marginTop: 16, paddingTop: 16 }}>
            <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--g400)" }}>
              Logged in as<br />
              <span style={{ color: "var(--navy)", fontWeight: 600, fontSize: 13 }}>{adminUser?.email || "admin"}</span>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main style={{ padding: 28, overflow: "auto", maxWidth: 1100 }}>

          {/* ── OVERVIEW ── */}
          {adminTab === "overview" && (
            <div>
              <h1 style={{ color: "var(--navy)", marginBottom: 6, fontSize: 22, fontFamily: "var(--ff-d)" }}>Dashboard Overview</h1>
              <p style={{ color: "var(--g600)", fontSize: 14, marginBottom: 24 }}>Welcome back, {adminUser?.name || "Admin"}! Here's what's happening today.</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 14, marginBottom: 24 }}>
                {[
                  { label: "Total Users", val: mockStats.totalUsers.toLocaleString(), emoji: "👥", color: "var(--navy)", bg: "rgba(10,22,40,0.06)" },
                  { label: "Active Today", val: mockStats.activeToday, emoji: "🟢", color: "var(--green)", bg: "rgba(0,200,83,0.08)" },
                  { label: "Questions", val: mockStats.totalQuestions.toLocaleString(), emoji: "❓", color: "var(--orange)", bg: "rgba(255,107,0,0.08)" },
                  { label: "Total Revenue", val: `$${mockStats.revenue.toLocaleString()}`, emoji: "💰", color: "var(--navy)", bg: "rgba(10,22,40,0.06)" },
                  { label: "Pro Users", val: mockStats.proUsers, emoji: "⭐", color: "var(--orange)", bg: "rgba(255,107,0,0.08)" },
                  { label: "Premium", val: mockStats.premiumUsers, emoji: "👑", color: "var(--navy)", bg: "rgba(10,22,40,0.06)" },
                  { label: "This Month", val: `$${mockStats.revenueMonth.toLocaleString()}`, emoji: "📈", color: "var(--green)", bg: "rgba(0,200,83,0.08)" },
                ].map(({ label, val, emoji, color, bg }) => (
                  <div key={label} style={{ background: "white", borderRadius: "var(--r-md)", padding: "18px 16px", boxShadow: "var(--sh-sm)", border: "1px solid var(--g200)" }}>
                    <div style={{ width: 36, height: 36, background: bg, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, marginBottom: 10 }}>{emoji}</div>
                    <div style={{ fontSize: 22, fontFamily: "var(--ff-d)", fontWeight: 900, color, marginBottom: 2 }}>{val}</div>
                    <div style={{ fontSize: 12, color: "var(--g600)", fontWeight: 500 }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Card title="Quick Actions" icon="⚡">
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {[["View Users", "users", "var(--navy)"], ["API Settings", "api", "var(--orange)"], ["Payment Gateways", "payments", "var(--navy)"], ["SEO Settings", "seo", "var(--orange)"]].map(([label, tab, color]) => (
                      <button key={tab} onClick={() => setAdminTab(tab)}
                        style={{ padding: "10px 16px", background: "var(--off)", border: "1px solid var(--g200)", borderRadius: "var(--r-sm)", cursor: "pointer", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 13, color, textAlign: "left", transition: "all .2s", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--g100)"}
                        onMouseLeave={e => e.currentTarget.style.background = "var(--off)"}>
                        {label} <span>→</span>
                      </button>
                    ))}
                  </div>
                </Card>
                <Card title="System Status" icon="⚙️">
                  {[["API Connection", "Connected", "var(--green)"], ["Database", "Online", "var(--green)"], ["Payment Gateways", "3 Active", "var(--green)"], ["SSL Certificate", "Valid", "var(--green)"], ["reCAPTCHA", "Active", "var(--green)"]].map(([k, v, c]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid var(--g100)" }}>
                      <span style={{ fontSize: 13, color: "var(--g800)" }}>{k}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: c, background: `${c}15`, padding: "3px 10px", borderRadius: 99 }}>{v}</span>
                    </div>
                  ))}
                </Card>
              </div>
            </div>
          )}

          {/* ── USERS ── */}
          {adminTab === "users" && (
            <div>
              <h1 style={{ color: "var(--navy)", marginBottom: 6, fontSize: 22, fontFamily: "var(--ff-d)" }}>User Management</h1>
              <p style={{ color: "var(--g600)", fontSize: 14, marginBottom: 24 }}>View and manage all registered users.</p>
              <Card title={`All Users (${mockStats.totalUsers.toLocaleString()})`} icon="👥"
                footer={<button style={{ padding: "9px 18px", background: "var(--navy)", color: "white", border: "none", borderRadius: "var(--r-sm)", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Export CSV</button>}>
                <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                  <input type="search" placeholder="🔍  Search by name or email..." style={{ flex: 1, minWidth: 200, padding: "9px 14px", fontSize: 13, background: "var(--off)" }} />
                  <select style={{ width: "auto", padding: "9px 14px", fontSize: 13 }}>
                    <option>All Plans</option><option>Free</option><option>Pro</option><option>Premium</option>
                  </select>
                </div>
                <div style={{ borderRadius: "var(--r-sm)", overflow: "hidden", border: "1px solid var(--g200)" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr 80px", gap: 0, background: "var(--off)", padding: "10px 16px", borderBottom: "1px solid var(--g200)" }}>
                    {["Name", "Email", "Plan", "Joined", "Questions", ""].map(h => (
                      <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "var(--g600)", fontFamily: "var(--ff-d)", textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</div>
                    ))}
                  </div>
                  {[
                    { name: "User A", email: "user.a@example.com", plan: "Pro", joined: "2024-01-10", questions: 124 },
                    { name: "User B", email: "user.b@example.com", plan: "Free", joined: "2024-01-12", questions: 5 },
                    { name: "User C", email: "user.c@example.com", plan: "Premium", joined: "2024-01-08", questions: 341 },
                  ].map((u, i, arr) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr 80px", gap: 0, padding: "13px 16px", borderBottom: i < arr.length - 1 ? "1px solid var(--g200)" : "none", alignItems: "center", transition: "background .15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--off)"}
                      onMouseLeave={e => e.currentTarget.style.background = "white"}>
                      <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 14 }}>{u.name}</div>
                      <div style={{ color: "var(--g600)", fontSize: 13 }}>{u.email}</div>
                      <div><span style={{ background: u.plan === "Pro" ? "rgba(255,107,0,0.1)" : u.plan === "Premium" ? "rgba(10,22,40,0.08)" : "var(--g100)", color: u.plan === "Pro" ? "var(--orange)" : u.plan === "Premium" ? "var(--navy)" : "var(--g600)", borderRadius: 99, padding: "3px 10px", fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}>{u.plan}</span></div>
                      <div style={{ color: "var(--g600)", fontSize: 13 }}>{fmtDate(u.joined)}</div>
                      <div style={{ color: "var(--g600)", fontSize: 13 }}>{u.questions}</div>
                      <button style={{ padding: "5px 12px", background: "var(--navy)", color: "white", border: "none", borderRadius: "var(--r-sm)", cursor: "pointer", fontSize: 12, fontFamily: "var(--ff-d)", fontWeight: 600 }}>Edit</button>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {/* ── PRICING ── */}
          {adminTab === "pricing" && (
            <div>
              <h1 style={{ color: "var(--navy)", marginBottom: 6, fontSize: 22, fontFamily: "var(--ff-d)" }}>Pricing Management</h1>
              <p style={{ color: "var(--g600)", fontSize: 14, marginBottom: 24 }}>Configure plan prices and limits. Changes are saved to your database.</p>
              <Card title="Free Plan" icon="🆓"
                footer={<SaveBtn section="free-pricing" label="Save Free Plan" onClick={() => saveSection("free-pricing", { free_daily_limit: pricingSettings.freeDailyLimit }, "Free Plan")} />}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div><label>Price</label><input type="text" value="$0 / Free forever" disabled style={{ background: "var(--g100)", color: "var(--g600)" }} /></div>
                  <div><label htmlFor="free-limit">Daily Question Limit</label><input id="free-limit" type="number" min="1" max="50" value={pricingSettings.freeDailyLimit} onChange={e => setPricingSettings(p => ({ ...p, freeDailyLimit: e.target.value }))} /></div>
                </div>
              </Card>
              <Card title="Pro Plan ⭐" icon="🚀"
                footer={<SaveBtn section="pro-pricing" label="Save Pro Plan" onClick={() => saveSection("pro-pricing", { pro_monthly: pricingSettings.proMonthly, pro_yearly: pricingSettings.proYearly }, "Pro Plan")} />}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div><label htmlFor="pro-m">Monthly Price ($)</label><input id="pro-m" type="number" step="0.01" min="0" value={pricingSettings.proMonthly} onChange={e => setPricingSettings(p => ({ ...p, proMonthly: e.target.value }))} /></div>
                  <div><label htmlFor="pro-y">Yearly Price ($)</label><input id="pro-y" type="number" step="0.01" min="0" value={pricingSettings.proYearly} onChange={e => setPricingSettings(p => ({ ...p, proYearly: e.target.value }))} /></div>
                </div>
                <p style={{ fontSize: 12, color: "var(--g600)", marginTop: 10 }}>💡 Yearly price should be ~30% less than monthly × 12 (current: ${(pricingSettings.proMonthly * 12).toFixed(2)}/yr → ${(pricingSettings.proMonthly * 12 * 0.7).toFixed(2)} discounted)</p>
              </Card>
              <Card title="Premium Plan 👑" icon="💎"
                footer={<SaveBtn section="premium-pricing" label="Save Premium Plan" onClick={() => saveSection("premium-pricing", { premium_monthly: pricingSettings.premiumMonthly, premium_yearly: pricingSettings.premiumYearly }, "Premium Plan")} />}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div><label htmlFor="prem-m">Monthly Price ($)</label><input id="prem-m" type="number" step="0.01" min="0" value={pricingSettings.premiumMonthly} onChange={e => setPricingSettings(p => ({ ...p, premiumMonthly: e.target.value }))} /></div>
                  <div><label htmlFor="prem-y">Yearly Price ($)</label><input id="prem-y" type="number" step="0.01" min="0" value={pricingSettings.premiumYearly} onChange={e => setPricingSettings(p => ({ ...p, premiumYearly: e.target.value }))} /></div>
                </div>
              </Card>
            </div>
          )}

          {/* ── PAYMENTS ── */}
          {adminTab === "payments" && (
            <div>
              <h1 style={{ color: "var(--navy)", marginBottom: 6, fontSize: 22, fontFamily: "var(--ff-d)" }}>Payment Gateways</h1>
              <p style={{ color: "var(--g600)", fontSize: 14, marginBottom: 24 }}>Configure Stripe, PayPal, and Razorpay credentials. Keys are stored encrypted on the server.</p>
              <InfoBox>🔐 API keys are stored encrypted in your MySQL database. They are never exposed to users or in browser responses.</InfoBox>

              {/* Stripe */}
              <Card title="Stripe" icon="💳"
                footer={<><SecBtn label="Test Connection" loading={false} onClick={() => toast("Stripe test initiated", "ok")} /><SaveBtn section="stripe" label="Save Stripe" onClick={() => saveSection("stripe", { stripe_secret_key: paySettings.stripeSecretKey, stripe_webhook_secret: paySettings.stripeWebhookSecret, stripe_mode: paySettings.stripeMode }, "Stripe")} /></>}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 14 }}>
                  <KeyField id="stripe-key" label="Secret Key" stateKey="stripeKey" placeholder="sk_live_..." value={paySettings.stripeSecretKey} onChange={e => setPaySettings(p => ({ ...p, stripeSecretKey: e.target.value }))} hint="Found in Stripe Dashboard → Developers → API Keys" />
                  <KeyField id="stripe-wh" label="Webhook Secret" stateKey="stripeWh" placeholder="whsec_..." value={paySettings.stripeWebhookSecret} onChange={e => setPaySettings(p => ({ ...p, stripeWebhookSecret: e.target.value }))} hint="Create webhook at Stripe → Webhooks → Add endpoint" />
                  <div>
                    <label htmlFor="stripe-mode">Mode</label>
                    <select id="stripe-mode" value={paySettings.stripeMode} onChange={e => setPaySettings(p => ({ ...p, stripeMode: e.target.value }))}>
                      <option value="live">🟢 Live (Production)</option>
                      <option value="test">🟡 Test (Sandbox)</option>
                    </select>
                  </div>
                </div>
              </Card>

              {/* PayPal */}
              <Card title="PayPal" icon="🅿️"
                footer={<><SecBtn label="Test Connection" loading={false} onClick={() => toast("PayPal test initiated", "ok")} /><SaveBtn section="paypal" label="Save PayPal" onClick={() => saveSection("paypal", { paypal_client_id: paySettings.paypalClientId, paypal_client_secret: paySettings.paypalClientSecret, paypal_mode: paySettings.paypalMode }, "PayPal")} /></>}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 14 }}>
                  <KeyField id="pp-id" label="Client ID" stateKey="ppId" placeholder="AZ..." value={paySettings.paypalClientId} onChange={e => setPaySettings(p => ({ ...p, paypalClientId: e.target.value }))} hint="Found at developer.paypal.com → My Apps" />
                  <KeyField id="pp-secret" label="Client Secret" stateKey="ppSecret" placeholder="E..." value={paySettings.paypalClientSecret} onChange={e => setPaySettings(p => ({ ...p, paypalClientSecret: e.target.value }))} />
                  <div>
                    <label htmlFor="pp-mode">Mode</label>
                    <select id="pp-mode" value={paySettings.paypalMode} onChange={e => setPaySettings(p => ({ ...p, paypalMode: e.target.value }))}>
                      <option value="live">🟢 Live (Production)</option>
                      <option value="sandbox">🟡 Sandbox (Testing)</option>
                    </select>
                  </div>
                </div>
              </Card>

              {/* Razorpay */}
              <Card title="Razorpay" icon="₹"
                footer={<><SecBtn label="Test Connection" loading={false} onClick={() => toast("Razorpay test initiated", "ok")} /><SaveBtn section="razorpay" label="Save Razorpay" onClick={() => saveSection("razorpay", { razorpay_key_id: paySettings.razorpayKeyId, razorpay_key_secret: paySettings.razorpayKeySecret }, "Razorpay")} /></>}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 14 }}>
                  <KeyField id="rp-id" label="Key ID" stateKey="rpId" placeholder="rzp_live_..." value={paySettings.razorpayKeyId} onChange={e => setPaySettings(p => ({ ...p, razorpayKeyId: e.target.value }))} hint="Found at razorpay.com → Settings → API Keys" />
                  <KeyField id="rp-secret" label="Key Secret" stateKey="rpSecret" placeholder="Your key secret" value={paySettings.razorpayKeySecret} onChange={e => setPaySettings(p => ({ ...p, razorpayKeySecret: e.target.value }))} />
                </div>
              </Card>
            </div>
          )}

          {/* ── API SETTINGS ── */}
          {adminTab === "api" && (
            <div>
              <h1 style={{ color: "var(--navy)", marginBottom: 6, fontSize: 22, fontFamily: "var(--ff-d)" }}>API Configuration</h1>
              <p style={{ color: "var(--g600)", fontSize: 14, marginBottom: 24 }}>Configure your OpenAI API key and settings. Keys are stored securely on the server only.</p>
              <InfoBox>🔌 Your OpenAI API key is stored encrypted in the MySQL database and only accessed server-side via PHP. It is <strong>never sent to the browser</strong>.</InfoBox>

              <Card title="OpenAI API Key" icon="🤖"
                footer={<SaveBtn section="openai-key" label="Save OpenAI Key" onClick={() => saveSection("openai-key", { openai_api_key: apiSettings.openaiApiKey }, "OpenAI API Key")} />}>
                <KeyField id="oai-key" label="OpenAI Secret Key" stateKey="openaiKey"
                  placeholder="sk-proj-..." value={apiSettings.openaiApiKey}
                  onChange={e => setApiSettings(a => ({ ...a, openaiApiKey: e.target.value }))}
                  hint="Get your key at platform.openai.com → API Keys → Create new secret key" />
                <div style={{ marginTop: 16, padding: "12px 16px", background: "var(--g100)", borderRadius: "var(--r-sm)", fontSize: 12, color: "var(--g600)" }}>
                  <strong>After saving:</strong> Your PHP backend reads this key from the database via <code style={{ background: "white", padding: "1px 5px", borderRadius: 3 }}>getSetting('openai_api_key')</code> — it never touches the frontend.
                </div>
              </Card>

              <Card title="Model & Behaviour" icon="⚙️"
                footer={<SaveBtn section="api-model" label="Save Model Settings" onClick={() => saveSection("api-model", { openai_model: apiSettings.openaiModel, openai_max_tokens: apiSettings.maxTokens, openai_temperature: apiSettings.temperature, system_prompt: apiSettings.systemPrompt }, "Model Settings")} />}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 16 }}>
                  <div>
                    <label htmlFor="ai-model">AI Model</label>
                    <select id="ai-model" value={apiSettings.openaiModel} onChange={e => setApiSettings(a => ({ ...a, openaiModel: e.target.value }))}>
                      <option value="gpt-4o-mini">GPT-4o Mini (Recommended — fast & cheap)</option>
                      <option value="gpt-4o">GPT-4o (Best quality)</option>
                      <option value="gpt-4-turbo">GPT-4 Turbo</option>
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Cheapest)</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ai-tokens">Max Tokens</label>
                    <input id="ai-tokens" type="number" min="100" max="4000" value={apiSettings.maxTokens} onChange={e => setApiSettings(a => ({ ...a, maxTokens: e.target.value }))} />
                    <p style={{ fontSize: 11, color: "var(--g600)", marginTop: 4 }}>💡 1000 = ~750 words per answer</p>
                  </div>
                  <div>
                    <label htmlFor="ai-temp">Temperature (0–1)</label>
                    <input id="ai-temp" type="number" min="0" max="1" step="0.1" value={apiSettings.temperature} onChange={e => setApiSettings(a => ({ ...a, temperature: e.target.value }))} />
                    <p style={{ fontSize: 11, color: "var(--g600)", marginTop: 4 }}>💡 0.7 = balanced accuracy + creativity</p>
                  </div>
                </div>
                <div>
                  <label htmlFor="sys-prompt">System Prompt (AI Personality)</label>
                  <textarea id="sys-prompt" rows={4} value={apiSettings.systemPrompt} onChange={e => setApiSettings(a => ({ ...a, systemPrompt: e.target.value }))} placeholder="You are an expert biology tutor..." />
                </div>
              </Card>

              <Card title="Rate Limiting" icon="⏱️"
                footer={<SaveBtn section="rate-limit" label="Save Limits" onClick={() => saveSection("rate-limit", { free_questions_per_day: apiSettings.freeQuestionsPerDay }, "Rate Limits")} />}>
                <div style={{ maxWidth: 300 }}>
                  <label htmlFor="free-qs">Free Questions Per Day (per user/IP)</label>
                  <input id="free-qs" type="number" min="1" max="100" value={apiSettings.freeQuestionsPerDay} onChange={e => setApiSettings(a => ({ ...a, freeQuestionsPerDay: e.target.value }))} />
                  <p style={{ fontSize: 12, color: "var(--g600)", marginTop: 8 }}>Users on paid plans get unlimited questions. This only applies to free/guest users.</p>
                </div>
              </Card>
            </div>
          )}

          {/* ── SEO ── */}
          {adminTab === "seo" && (
            <div>
              <h1 style={{ color: "var(--navy)", marginBottom: 6, fontSize: 22, fontFamily: "var(--ff-d)" }}>SEO & Analytics</h1>
              <p style={{ color: "var(--g600)", fontSize: 14, marginBottom: 24 }}>Configure meta tags, Google Analytics, Search Console, robots.txt and sitemap.</p>

              <Card title="Site Identity" icon="🌐"
                footer={<SaveBtn section="site-identity" label="Save Site Settings" onClick={() => saveSection("site-identity", { site_name: seoSettings.siteName, site_url: seoSettings.siteUrl, meta_title: seoSettings.metaTitle, meta_description: seoSettings.metaDesc, meta_keywords: seoSettings.metaKeywords }, "Site Settings")} />}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div><label htmlFor="sn">Site Name</label><input id="sn" value={seoSettings.siteName} onChange={e => setSeoSettings(s => ({ ...s, siteName: e.target.value }))} /></div>
                  <div><label htmlFor="su">Site URL</label><input id="su" type="url" value={seoSettings.siteUrl} onChange={e => setSeoSettings(s => ({ ...s, siteUrl: e.target.value }))} /></div>
                  <div style={{ gridColumn: "1/-1" }}><label htmlFor="mt">Default Meta Title</label><input id="mt" value={seoSettings.metaTitle} onChange={e => setSeoSettings(s => ({ ...s, metaTitle: e.target.value }))} /><p style={{ fontSize: 11, color: seoSettings.metaTitle.length > 60 ? "var(--red)" : "var(--g600)", marginTop: 4 }}>{seoSettings.metaTitle.length}/60 characters {seoSettings.metaTitle.length > 60 ? "⚠️ Too long!" : "✓"}</p></div>
                  <div style={{ gridColumn: "1/-1" }}><label htmlFor="md">Meta Description</label><textarea id="md" rows={2} value={seoSettings.metaDesc} onChange={e => setSeoSettings(s => ({ ...s, metaDesc: e.target.value }))} /><p style={{ fontSize: 11, color: seoSettings.metaDesc.length > 160 ? "var(--red)" : "var(--g600)", marginTop: 4 }}>{seoSettings.metaDesc.length}/160 characters {seoSettings.metaDesc.length > 160 ? "⚠️ Too long!" : "✓"}</p></div>
                  <div style={{ gridColumn: "1/-1" }}><label htmlFor="mk">Meta Keywords</label><input id="mk" value={seoSettings.metaKeywords} onChange={e => setSeoSettings(s => ({ ...s, metaKeywords: e.target.value }))} /></div>
                </div>
              </Card>

              <Card title="Google Analytics (GA4)" icon="📊"
                footer={<SaveBtn section="ga" label="Save Analytics" onClick={() => saveSection("ga", { google_analytics_id: seoSettings.googleAnalyticsId, gtm_id: seoSettings.gtmId }, "Google Analytics")} />}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div><label htmlFor="ga-id">GA4 Measurement ID</label><input id="ga-id" placeholder="G-XXXXXXXXXX" value={seoSettings.googleAnalyticsId} onChange={e => setSeoSettings(s => ({ ...s, googleAnalyticsId: e.target.value }))} /><p style={{ fontSize: 11, color: "var(--g600)", marginTop: 4 }}>💡 Get from analytics.google.com → Admin → Data Streams</p></div>
                  <div><label htmlFor="gtm-id">Google Tag Manager ID</label><input id="gtm-id" placeholder="GTM-XXXXXXX" value={seoSettings.gtmId} onChange={e => setSeoSettings(s => ({ ...s, gtmId: e.target.value }))} /></div>
                </div>
              </Card>

              <Card title="Google Search Console" icon="🔍"
                footer={<SaveBtn section="gsc" label="Save Search Console" onClick={() => saveSection("gsc", { google_search_console: seoSettings.googleSearchConsole }, "Search Console")} />}>
                <div><label htmlFor="gsc">Verification Code (content= value only)</label><input id="gsc" placeholder="Paste the content=&quot;...&quot; value from the HTML tag" value={seoSettings.googleSearchConsole} onChange={e => setSeoSettings(s => ({ ...s, googleSearchConsole: e.target.value }))} /><p style={{ fontSize: 11, color: "var(--g600)", marginTop: 4 }}>💡 From Search Console → Settings → Ownership verification → HTML tag → copy only the content="..." value</p></div>
              </Card>

              <Card title="robots.txt" icon="🤖"
                footer={<SaveBtn section="robots" label="Save robots.txt" onClick={() => saveSection("robots", { robots_txt: seoSettings.robotsTxt }, "robots.txt")} />}>
                <textarea rows={8} value={seoSettings.robotsTxt} onChange={e => setSeoSettings(s => ({ ...s, robotsTxt: e.target.value }))} style={{ fontFamily: "monospace", fontSize: 13, background: "var(--off)" }} />
              </Card>

              <Card title="Sitemap" icon="🗺️"
                footer={<><button style={{ padding: "10px 20px", background: "var(--navy)", color: "white", border: "none", borderRadius: "var(--r-sm)", fontFamily: "var(--ff-d)", fontWeight: 700, fontSize: 13, cursor: "pointer" }} onClick={() => toast("Sitemap regenerated ✓", "ok")}>Regenerate Sitemap</button><SaveBtn section="sitemap" label="Save Sitemap Settings" onClick={() => saveSection("sitemap", { sitemap_frequency: seoSettings.sitemapFrequency }, "Sitemap")} /></>}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <div><label>Sitemap URL (auto-generated)</label><input value={`${seoSettings.siteUrl}/sitemap.xml`} disabled style={{ background: "var(--g100)", color: "var(--g600)" }} /></div>
                  <div><label htmlFor="sm-freq">Update Frequency</label>
                    <select id="sm-freq" value={seoSettings.sitemapFrequency} onChange={e => setSeoSettings(s => ({ ...s, sitemapFrequency: e.target.value }))}>
                      <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* ── SECURITY ── */}
          {adminTab === "security" && (
            <div>
              <h1 style={{ color: "var(--navy)", marginBottom: 6, fontSize: 22, fontFamily: "var(--ff-d)" }}>Security Settings</h1>
              <p style={{ color: "var(--g600)", fontSize: 14, marginBottom: 24 }}>Manage admin passwords and create additional admin accounts.</p>
              <InfoBox>🔐 Admin credentials are stored with bcrypt hashing on the server. Never share your password or API keys with anyone. All admin actions are logged.</InfoBox>

              <Card title="Change Admin Password" icon="🔑"
                footer={<SaveBtn section="password" label="Update Password" onClick={savePassword} />}>
                <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 440 }}>
                  <div><label htmlFor="sec-cur">Current Password</label><input id="sec-cur" type="password" placeholder="Your current password" autoComplete="current-password" value={secForm.curPass} onChange={e => setSecForm(f => ({ ...f, curPass: e.target.value }))} /></div>
                  <div><label htmlFor="sec-new">New Password</label><input id="sec-new" type="password" placeholder="Min. 8 characters" autoComplete="new-password" value={secForm.newPass} onChange={e => setSecForm(f => ({ ...f, newPass: e.target.value }))} /><p style={{ fontSize: 11, color: "var(--g600)", marginTop: 4 }}>Must be at least 8 characters with a mix of letters and numbers.</p></div>
                  <div><label htmlFor="sec-conf">Confirm New Password</label><input id="sec-conf" type="password" placeholder="Repeat new password" autoComplete="new-password" value={secForm.confPass} onChange={e => setSecForm(f => ({ ...f, confPass: e.target.value }))}
                    style={{ borderColor: secForm.confPass && secForm.newPass !== secForm.confPass ? "var(--red)" : undefined }} />
                    {secForm.confPass && secForm.newPass !== secForm.confPass && <p style={{ fontSize: 11, color: "var(--red)", marginTop: 4 }}>⚠️ Passwords do not match</p>}
                  </div>
                </div>
              </Card>

              <Card title="Create New Admin Account" icon="👤"
                footer={<SaveBtn section="newadmin" label="Create Admin Account" onClick={createAdmin} />}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14 }}>
                  <div><label htmlFor="na-name">Full Name</label><input id="na-name" type="text" placeholder="Admin name" value={newAdmin.name} onChange={e => setNewAdmin(a => ({ ...a, name: e.target.value }))} /></div>
                  <div><label htmlFor="na-email">Email Address</label><input id="na-email" type="email" placeholder="admin@yourdomain.com" value={newAdmin.email} onChange={e => setNewAdmin(a => ({ ...a, email: e.target.value }))} /></div>
                  <div><label htmlFor="na-pass">Password</label><input id="na-pass" type="password" placeholder="Min. 8 characters" value={newAdmin.password} onChange={e => setNewAdmin(a => ({ ...a, password: e.target.value }))} /></div>
                  <div><label htmlFor="na-role">Role</label>
                    <select id="na-role" value={newAdmin.role} onChange={e => setNewAdmin(a => ({ ...a, role: e.target.value }))}>
                      <option>Super Admin</option><option>Content Admin</option><option>Support</option>
                    </select>
                  </div>
                </div>
                <p style={{ fontSize: 12, color: "var(--g600)", marginTop: 12 }}>New admin will receive login credentials via email after creation.</p>
              </Card>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   ROOT APP
══════════════════════════════════════════════ */
export default function App() {
  const [page, setPage] = useState("home");
  const [user, setUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [adminAuthed, setAdminAuthed] = useState(false);

  const showToast = (msg, type = "ok") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };
  const logout = () => { setUser(null); setPage("home"); showToast("Logged out successfully", "ok"); };
  const onAuth = (u) => setUser(u);
  const onAdminAuth = (a) => { setAdminUser(a); setAdminAuthed(true); setPage("admin"); };
  const onAdminLogout = () => { setAdminUser(null); setAdminAuthed(false); localStorage.removeItem("adminToken"); localStorage.removeItem("adminUser"); localStorage.removeItem("gotoAdmin"); setPage("home"); showToast("Admin signed out", "ok"); };

  // Auto-login admin if redirected from admin-login.html
  useEffect(() => {
    const go = localStorage.getItem("gotoAdmin");
    const token = localStorage.getItem("adminToken");
    const stored = localStorage.getItem("adminUser");
    if (go === "1" && token && stored) {
      try {
        const adminData = JSON.parse(stored);
        setAdminUser(adminData);
        setAdminAuthed(true);
        setPage("admin");
        localStorage.removeItem("gotoAdmin");
      } catch (e) {
        localStorage.removeItem("gotoAdmin");
      }
    }
  }, []);

  const ProviderWrap = ({ children }) => (
    <ToastCtx.Provider value={{ toast: showToast }}>
      <AuthCtx.Provider value={{ user, logout }}>
        <SEOHead page={page} />
        {children}
        {toast && <div role="alert" aria-live="assertive" className={`toast toast-${toast.type === "ok" ? "ok" : "err"}`}>{toast.msg}</div>}
      </AuthCtx.Provider>
    </ToastCtx.Provider>
  );

  // Admin route — shows login first, then panel
  if (page === "admin") return (
    <ProviderWrap>
      {adminAuthed
        ? <AdminPanel setPage={setPage} adminUser={adminUser} onAdminLogout={onAdminLogout} />
        : <AdminLogin onAdminAuth={onAdminAuth} />}
    </ProviderWrap>
  );

  if (page === "login" || page === "signup") return <ProviderWrap><Navbar page={page} setPage={setPage} /><AuthPage mode={page} setPage={setPage} onAuth={onAuth} /></ProviderWrap>;
  if (page === "dashboard") return <ProviderWrap><Navbar page={page} setPage={setPage} /><Dashboard setPage={setPage} /></ProviderWrap>;
  if (page === "checkout") return <ProviderWrap><Navbar page={page} setPage={setPage} /><CheckoutPage setPage={setPage} /></ProviderWrap>;

  return (
    <ProviderWrap>
      <Navbar page={page} setPage={setPage} />
      <main id="main-content">
        <Hero setPage={setPage} />
        <Features />
        <TutorSection setPage={setPage} />
        <TopicsSection />
        <PricingSection setPage={setPage} />
        <FAQSection />
        <Testimonials />
        <CTASection setPage={setPage} />
      </main>
      <Footer setPage={setPage} />
    </ProviderWrap>
  );
}